// PlacesView.jsx — View PURA da /places. O nome da tela vem do nicho (vm.labels): "Locais",
// "Estabelecimentos", "Consultórios"… A View nunca escreve "Local" por conta própria.
// Contrato: places.vm.js (@version 2). Recebe { vm, actions }: sem fetch, sem formatação, sem regra de
// negócio — veredito, pendências, rótulos e links (mapa, tel:, wa.me, mailto, site) chegam prontos.
//
// ═══════════════════════════════════════════════════════════════════════════════
// A PERGUNTA DA TELA: "este lugar consegue receber agendamento — e o que acontece nele?"
//
// 1. O VEREDITO (readiness) abre tudo, com os três pesos de Agendas:
//    · recebe            → CALMO: uma linha verde discreta. Se só parte não aparece (status warning),
//                          a linha continua verde e o "mas…" vem logo abaixo, em âmbar.
//    · não recebe        → ALTO: faixa de tint no topo, com a frase do vm, as pendências e o conserto
//                          ali mesmo (issues[].fix → onFix).
//    · escolha do dono   → SERENO: pausa e texto neutro. Nunca vermelho.
//    "Sem endereço" (info) não rebaixa o veredito: vira informação calma no lugar do endereço.
//
// 2. UM LUGAR SÓ (918 de 969 contas): desde a v2 o container já o manda ABERTO (selectedId + detail), sem
//    "voltar" — não há lista para onde voltar. Se um dia vier sem detail, ele vira um RETRATO de largura
//    cheia (quem atende, o que se oferece, como pagar, o movimento) com o caminho para abrir o resto.
//    Sem faixa de resumo nem filtros: não há o que comparar.
//
// 3. VÁRIOS LUGARES: a faixa de resumo (a mesma régua de Agendas: um traço por lugar, o que não
//    recebe é mais alto e hachurado), os filtros (busca, ordem e segmentos com contagem) e a grade.
//    A ordem é a que o dono escolheu (o vm manda ordenado); quem não recebe salta pela faixa tingida,
//    e o "1 não recebe agendamento" do resumo leva até ele.
//
// 4. O LUGAR ABERTO (selectedId + detail) ocupa a página no lugar da lista, com volta e troca rápida
//    entre lugares. Em largura cheia reflui em colunas: à esquerda o que o lugar É (equipe, quem faz o
//    quê, preços); à direita o que ele VIVE (agendas com a fita da semana, movimento, páginas,
//    ausências). Em ~1.600 px, equipe e "quem faz o quê" ficam lado a lado, com os preços embaixo.
//    · QUEM FAZ O QUÊ: matriz serviço × profissional; o serviço que ninguém faz vira uma linha tingida
//      e hachurada. Em largura estreita (ou equipe grande) vira lista com os rostos.
//    · PREÇOS: matriz serviço × forma de pagamento. Igual ao padrão (isDefault) fica quieto, diferente
//      fica forte, "a combinar" (isToBeAgreed) fica em itálico, e o preço próprio de um profissional ganha
//      uma estrela com a frase do vm numa nota. Nada disso sai do texto do rótulo (v2). No celular, cada
//      serviço vira uma lista forma → preço.
//    · SERVIÇO QUE NÃO DÁ PARA AGENDAR (v2): o PESO vem de notBookableStatus (warning/error = defeito,
//      tingido; neutral = pausa do dono, calmo) e o ÍCONE de hasNoProfessional ("ninguém" × "sem horário";
//      a pausa leva o ícone de pausa).
//    · AGUARDANDO CONFIRMAÇÃO (v2): é uma ação para o dono, não um defeito do lugar — cor de ação (primária
//      suave), no cartão, no topo do lugar aberto e no Movimento; leva a Agendamentos.
//    · Sem formas informadas: informação calma (o cliente vê o preço padrão) e a lista dos padrões.
//
// 5. Foco e rolagem (as lições da Gestão de Acessos): ao abrir, o foco vai para o título do lugar e ele
//    aparece sem pular a página; ao fechar, o foco volta para quem abriu. O "Por que isso importa?" que
//    abre rola só o necessário. Tudo o que rola deixa 80 px de folga no pé (o aviso do container) e a
//    página termina com essa mesma folga. No primeiro desenho nada rola nem recebe foco.
//
// Consistência: CARD_SX, TINT, botões, fita da semana, régua, StateBlock e filtros iguais a Agendas e
// Serviços; avatar com a foto por cima das iniciais igual à Gestão de Acessos. Lugar não tem foto:
// monograma QUADRADO (pessoa é redonda). Neutros de --bg-page/--divider/--text-*; nunca --slate-*.
import { Fragment, useState, useRef, useEffect } from 'react';
import {
  Box, Typography, Button, ButtonBase, IconButton, Tooltip, TextField, InputAdornment,
  Select, MenuItem, Skeleton, Collapse,
} from '@mui/material';
import StorefrontRounded from '@mui/icons-material/StorefrontRounded';
import AddRounded from '@mui/icons-material/AddRounded';
import SearchRounded from '@mui/icons-material/SearchRounded';
import SortRounded from '@mui/icons-material/SortRounded';
import FilterAltOffRounded from '@mui/icons-material/FilterAltOffRounded';
import SearchOffRounded from '@mui/icons-material/SearchOffRounded';
import ReplayRounded from '@mui/icons-material/ReplayRounded';
import ErrorRounded from '@mui/icons-material/ErrorRounded';
import EditRounded from '@mui/icons-material/EditRounded';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import CheckRounded from '@mui/icons-material/CheckRounded';
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import PauseCircleRounded from '@mui/icons-material/PauseCircleRounded';
import EventBusyRounded from '@mui/icons-material/EventBusyRounded';
import EventAvailableRounded from '@mui/icons-material/EventAvailableRounded';
import CalendarMonthRounded from '@mui/icons-material/CalendarMonthRounded';
import AccessTimeRounded from '@mui/icons-material/AccessTimeRounded';
import LocationOffRounded from '@mui/icons-material/LocationOffRounded';
import PlaceRounded from '@mui/icons-material/PlaceRounded';
import MapRounded from '@mui/icons-material/MapRounded';
import ContentCopyRounded from '@mui/icons-material/ContentCopyRounded';
import CallRounded from '@mui/icons-material/CallRounded';
import WhatsApp from '@mui/icons-material/WhatsApp';
import MailOutlineRounded from '@mui/icons-material/MailOutlineRounded';
import LanguageRounded from '@mui/icons-material/LanguageRounded';
import PublicRounded from '@mui/icons-material/PublicRounded';
import OpenInNewRounded from '@mui/icons-material/OpenInNewRounded';
import PeopleAltRounded from '@mui/icons-material/PeopleAltRounded';
import PersonOffRounded from '@mui/icons-material/PersonOffRounded';
import HandshakeRounded from '@mui/icons-material/HandshakeRounded';
import DesignServicesRounded from '@mui/icons-material/DesignServicesRounded';
import PaymentsRounded from '@mui/icons-material/PaymentsRounded';
import PixRounded from '@mui/icons-material/PixRounded';
import CreditCardRounded from '@mui/icons-material/CreditCardRounded';
import HealthAndSafetyRounded from '@mui/icons-material/HealthAndSafetyRounded';
import ReceiptLongRounded from '@mui/icons-material/ReceiptLongRounded';
import SellRounded from '@mui/icons-material/SellRounded';
import WorkspacePremiumRounded from '@mui/icons-material/WorkspacePremiumRounded';
import AutoAwesomeRounded from '@mui/icons-material/AutoAwesomeRounded';
import BeachAccessRounded from '@mui/icons-material/BeachAccessRounded';
import GroupsRounded from '@mui/icons-material/GroupsRounded';
import InsightsRounded from '@mui/icons-material/InsightsRounded';
import StarRounded from '@mui/icons-material/StarRounded';
import TuneRounded from '@mui/icons-material/TuneRounded';
import HourglassTopRounded from '@mui/icons-material/HourglassTopRounded';

/* ═══════════════════════════════════════════════════════════════════════════
   Tokens locais (os mesmos papéis das telas irmãs)
   ═══════════════════════════════════════════════════════════════════════════ */

const CARD_SX = {
  bgcolor: 'var(--surface)',
  border: '1px solid',
  borderColor: 'var(--divider)',
  borderRadius: { xs: 'var(--radius-card-mobile)', sm: 'var(--radius-card)' },
  boxShadow: 'var(--shadow-card)',
};

const TINT = {
  success: { bg: 'var(--tint-success-bg)', border: 'var(--tint-success-border)', fg: 'var(--tint-success-fg)', sub: 'var(--tint-success-sub)', main: 'var(--color-success)' },
  warning: { bg: 'var(--tint-warning-bg)', border: 'var(--tint-warning-border)', fg: 'var(--tint-warning-fg)', sub: 'var(--tint-warning-sub)', main: 'var(--color-warning)' },
  error: { bg: 'var(--tint-error-bg)', border: 'var(--tint-error-border)', fg: 'var(--tint-error-fg)', sub: 'var(--tint-error-sub)', main: 'var(--color-error)' },
  info: { bg: 'var(--tint-info-bg)', border: 'var(--tint-info-border)', fg: 'var(--tint-info-fg)', sub: 'var(--tint-info-sub)', main: 'var(--color-info)' },
  neutral: { bg: 'var(--bg-page)', border: 'var(--divider)', fg: 'var(--text-secondary)', sub: 'var(--text-secondary)', main: 'var(--text-secondary)' },
};
const tint = (tone) => TINT[tone] || TINT.neutral;

// CTA de ativação (o primeiro lugar), como o vazio de Agendas. Fallback no verde escuro que já existe.
const CTA_FILL = 'var(--color-cta, var(--tint-success-fg))';
const CTA_FILL_DARK = 'var(--color-cta-dark, color-mix(in srgb, var(--color-cta, var(--tint-success-fg)) 86%, var(--text-primary)))';

const HEAD = { fontWeight: 'var(--fw-bold)' };
const NUM = { fontVariantNumeric: 'tabular-nums' };
const ONE_LINE = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
const BREAK = { overflowWrap: 'anywhere' };
// Em sx, width: 1 é 100% (não 1 px): por isso as medidas vão em string.
const SR_ONLY = { position: 'absolute', width: '1px', height: '1px', m: '-1px', p: 0, border: 0, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' };
const FOCUS = { '&:focus-visible': { outline: '2px solid var(--color-primary)', outlineOffset: 2 } };
const REDUCED = '@media (prefers-reduced-motion: reduce)';
const LIST_RESET = { listStyle: 'none', m: 0, p: 0 };
const OVERLINE = { color: 'var(--text-secondary)', fontWeight: 'var(--fw-bold)', letterSpacing: '.08em' };

// Folga do aviso do container (Snackbar no pé: 56 px no celular, 72 px a partir de 600 px). 80 px =
// 2 × --space-4 + --space-2. Margem de rolagem de tudo que a View leva à vista, e o fim da página.
const FOLGA = 'calc(var(--space-4) * 2 + var(--space-2))';
const SCROLL_MARGIN = { scrollMarginTop: FOLGA, scrollMarginBottom: FOLGA };

const PRIMARY_BTN_SX = {
  bgcolor: 'var(--color-primary)', color: 'var(--on-primary)',
  borderRadius: 'var(--radius-sm)', px: 2.25, minHeight: 42,
  fontWeight: 'var(--fw-bold)', textTransform: 'none', whiteSpace: 'nowrap',
  '&:hover': { bgcolor: 'var(--color-primary-dark)' },
  '&.Mui-disabled': { bgcolor: 'var(--color-primary-light)', color: 'var(--on-primary)', opacity: 0.8 },
  '&:focus-visible': { outline: '2px solid var(--color-primary-dark)', outlineOffset: 2 },
};

const CTA_BTN_SX = {
  bgcolor: CTA_FILL, color: 'var(--on-primary)',
  borderRadius: 'var(--radius-sm)', px: 2.5, minHeight: 46,
  fontWeight: 'var(--fw-bold)', textTransform: 'none', whiteSpace: 'nowrap',
  '&:hover': { bgcolor: CTA_FILL_DARK },
  '&:focus-visible': { outline: `2px solid ${CTA_FILL_DARK}`, outlineOffset: 2 },
};

const OUTLINE_BTN_SX = {
  minHeight: 40, px: 1.75, borderRadius: 'var(--radius-sm)', textTransform: 'none',
  border: '1px solid', borderColor: 'var(--divider)', bgcolor: 'var(--surface)',
  color: 'var(--text-secondary)', fontWeight: 'var(--fw-medium)', whiteSpace: 'nowrap',
  '&:hover': { borderColor: 'var(--color-primary)', color: 'var(--color-primary-dark)', bgcolor: 'var(--color-primary-soft)' },
  '&.Mui-disabled': { color: 'var(--text-disabled)', borderColor: 'var(--divider)' },
  ...FOCUS,
};

const QUIET_BTN_SX = {
  minHeight: 40, px: 1.25, borderRadius: 'var(--radius-sm)', textTransform: 'none',
  color: 'var(--text-secondary)', fontWeight: 'var(--fw-medium)', whiteSpace: 'nowrap',
  '&:hover': { color: 'var(--text-primary)', bgcolor: 'var(--bg-page)' },
  ...FOCUS,
};

const LINK_BTN_SX = {
  minHeight: 40, px: 1.25, borderRadius: 'var(--radius-sm)', textTransform: 'none',
  color: 'var(--color-primary-dark)', fontWeight: 'var(--fw-bold)', whiteSpace: 'nowrap',
  '&:hover': { bgcolor: 'var(--color-primary-soft)' },
  ...FOCUS,
};

const ICON_BTN_SX = {
  width: 40, height: 40, color: 'var(--text-secondary)',
  '&:hover': { color: 'var(--color-primary-dark)', bgcolor: 'var(--color-primary-soft)' },
  '&.Mui-disabled': { color: 'var(--text-disabled)' },
  ...FOCUS,
};

const DANGER_ICON_BTN_SX = {
  ...ICON_BTN_SX,
  '&:hover': { color: 'var(--color-error)', bgcolor: 'var(--tint-error-bg)' },
};

// Título que recebe foco por código (tabIndex -1): anel só quando o foco é de teclado.
const FOCUS_TITLE_SX = {
  borderRadius: 'var(--radius-sm)',
  '&:focus': { outline: 'none' },
  '&:focus-visible': { outline: '2px solid var(--color-primary)', outlineOffset: '3px' },
};

const rise = (delay = 0) => ({
  '@keyframes plRise': {
    from: { opacity: 0, transform: 'translateY(10px)' },
    to: { opacity: 1, transform: 'none' },
  },
  animation: 'plRise .5s cubic-bezier(.2,.7,.3,1) both',
  animationDelay: `${delay}ms`,
  [REDUCED]: { animation: 'none' },
});

// Hachura = "não chega ao cliente". Mesmo desenho da régua e da fita de Agendas.
const hatch = (c) => `repeating-linear-gradient(135deg, ${c} 0 3px, color-mix(in srgb, ${c} 30%, transparent) 3px 6px)`;

const DAY_INITIAL = ['S', 'T', 'Q', 'Q', 'S', 'S', 'D'];
const DAY_NAME = ['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado', 'Domingo'];

/* O peso visual de um lugar sai SÓ de `readiness`. */
const pesoDe = (r) => (r?.isBookable ? (r.status === 'warning' ? 'partial' : 'live') : r?.isByChoice ? 'waiting' : 'alert');
const tomAlerta = (r) => (r?.status === 'warning' ? 'warning' : 'error');
const tomDoPeso = (r) => {
  const p = pesoDe(r);
  return p === 'alert' ? tomAlerta(r) : p === 'partial' ? 'warning' : p === 'waiting' ? 'neutral' : 'success';
};

const PAY_ICON = {
  pix: PixRounded, cash: PaymentsRounded, debitCard: CreditCardRounded, creditCard: CreditCardRounded,
  healthInsurance: HealthAndSafetyRounded, boleto: ReceiptLongRounded, other: SellRounded,
};
const payIcon = (type) => PAY_ICON[type] || SellRounded;

const ISSUE_ICON = { error: ErrorRounded, warning: WarningAmberRounded, info: InfoOutlined, neutral: PauseCircleRounded, success: CheckCircleRounded };
const ISSUE_SR = { error: 'Impede de agendar', warning: 'Atenção', info: 'Vale saber', neutral: 'Escolha sua', success: 'Em ordem' };
const issueTone = (i) => (i?.isByChoice ? 'neutral' : i?.status || 'neutral');
// Títulos fixos de seção (vocabulário da View, como "Por que isso importa?" em Agendas).
const tituloPendencias = (r) => {
  if (pesoDe(r) === 'alert') return 'O que falta para receber agendamento';
  return (r?.issues || []).some((i) => !i.isByChoice && (i.status === 'error' || i.status === 'warning')) ? 'O que ajustar' : 'Vale saber';
};

// Serviço que não dá para agendar: o PESO vem do status (v2) e o ÍCONE de hasNoProfessional.
const pesoServico = (it) => {
  if (!it || it.isBookable) return null;
  if (it.notBookableIsByChoice) return 'neutral';
  return it.notBookableStatus || (it.hasNoProfessional ? 'error' : 'neutral');
};
const ehDefeito = (tom) => tom === 'warning' || tom === 'error';
const iconeServico = (it) => (it.notBookableIsByChoice ? PauseCircleRounded : it.hasNoProfessional ? PersonOffRounded : EventBusyRounded);

/* ── Foco e rolagem (só comportamento de UI) ──────────────────────────── */
const semMovimento = () => {
  try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { return true; }
};
// Rola só o necessário para o elemento aparecer inteiro (block 'nearest' + a folga do scroll-margin).
function mostrar(el) {
  if (el && typeof el.scrollIntoView === 'function') {
    el.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: semMovimento() ? 'auto' : 'smooth' });
  }
}
// O foco "caiu na página": ninguém focado, ou o elemento focado saiu da tela.
const focoPerdido = () => {
  const ae = typeof document !== 'undefined' ? document.activeElement : null;
  return !ae || ae === document.body || ae === document.documentElement || ae.isConnected === false;
};
// O lugar aberto é mais alto que a janela, e 'nearest' não rola um elemento que já cobre a tela. Então,
// ao abrir, o que vem à vista é o COMEÇO dele (o veredito vem antes do nome): só se o começo estiver acima
// da janela ou lá embaixo; se já está no alto da tela, nada se mexe.
function mostrarInicio(el) {
  if (!el || typeof el.scrollIntoView !== 'function') return;
  const topo = el.getBoundingClientRect().top;
  const margem = parseFloat(getComputedStyle(el).scrollMarginTop) || 0;
  if (topo < margem || topo > window.innerHeight / 2) {
    el.scrollIntoView({ block: 'start', behavior: semMovimento() ? 'auto' : 'smooth' });
  }
}
function levarFoco(el, rolar = true) {
  if (!el || typeof el.focus !== 'function') return;
  el.focus({ preventScroll: true });
  if (rolar) mostrar(el);
}

/* ═══════════════════════════════════════════════════════════════════════════
   Peças pequenas
   ═══════════════════════════════════════════════════════════════════════════ */

/** Monograma do lugar — quadrado (lugar não tem foto; pessoa é redonda). */
function Monogram({ initials, size = 48 }) {
  return (
    <Box
      aria-hidden="true"
      sx={{
        width: size, height: size, flexShrink: 0, borderRadius: 'var(--radius-sm)',
        bgcolor: 'var(--color-primary-soft)', color: 'var(--color-primary-dark)',
        border: '1px solid', borderColor: 'color-mix(in srgb, var(--color-primary) 22%, transparent)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <Typography component="span" variant={size >= 56 ? 'h6' : 'subtitle2'} sx={{ ...HEAD, color: 'inherit', letterSpacing: '.04em', lineHeight: 1 }}>
        {initials}
      </Typography>
    </Box>
  );
}

/** Pessoa: a foto por cima das iniciais; se a foto falhar (onError), fica a inicial. */
function Avatar({ photoUrl, initials, size = 32, ring = false }) {
  const [broken, setBroken] = useState(false);
  return (
    <Box
      aria-hidden="true"
      sx={{
        position: 'relative', width: size, height: size, borderRadius: '50%', flexShrink: 0, overflow: 'hidden',
        bgcolor: 'var(--color-primary-soft)', display: 'flex', alignItems: 'center', justifyContent: 'center',
        ...(ring && { boxShadow: '0 0 0 2px var(--surface)' }),
      }}
    >
      <Typography component="span" variant={size >= 40 ? 'subtitle2' : 'caption'} sx={{ color: 'var(--color-primary-dark)', fontWeight: 'var(--fw-bold)', lineHeight: 1, letterSpacing: '.02em' }}>
        {initials}
      </Typography>
      {!!photoUrl && !broken && (
        <Box component="img" src={photoUrl} alt="" loading="lazy" onError={() => setBroken(true)} sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
      )}
    </Box>
  );
}

/** Rostos sobrepostos (até 5, na ordem do vm) + o "+2" pronto. */
function Faces({ members, moreLabel, size = 30 }) {
  const list = members || [];
  if (!list.length) return null;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
      <Box component="span" sx={SR_ONLY}>{list.map((m) => m.name).join(', ')}</Box>
      {list.map((m, i) => (
        <Tooltip key={m.id} title={m.name}>
          <Box sx={{ ml: i ? -0.75 : 0, display: 'flex', position: 'relative', zIndex: list.length - i }}>
            <Avatar photoUrl={m.photoUrl} initials={m.initials} size={size} ring />
          </Box>
        </Tooltip>
      ))}
      {moreLabel && (
        <Box aria-hidden="true" sx={{ ml: -0.75, height: size, minWidth: size, px: 0.75, borderRadius: 999, bgcolor: 'var(--bg-page)', boxShadow: '0 0 0 2px var(--surface)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="caption" sx={{ ...HEAD, color: 'var(--text-secondary)' }}>{moreLabel}</Typography>
        </Box>
      )}
    </Box>
  );
}

/** Linha de contexto: ícone discreto + texto JÁ ESCRITO pelo vm. */
function ContextLine({ icon, children, tone }) {
  const Icon = icon;
  const cor = tone ? tint(tone).fg : 'var(--text-secondary)';
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, minWidth: 0, color: cor }}>
      <Icon sx={{ fontSize: 16, flexShrink: 0, mt: '1px' }} aria-hidden="true" />
      <Typography variant="caption" sx={{ color: 'inherit', ...BREAK, textWrap: 'pretty' }}>{children}</Typography>
    </Box>
  );
}

/** Amostra de estado — o mesmo desenho na régua, nos números e na troca de lugar. */
function Swatch({ kind, tone = 'error', height = 10, width = 18 }) {
  const t = tint(tone);
  const base = { width, height, borderRadius: 3, flexShrink: 0, boxSizing: 'border-box' };
  if (kind === 'live' || kind === 'partial') return <Box aria-hidden="true" sx={{ ...base, bgcolor: 'var(--color-success)' }} />;
  if (kind === 'waiting') return <Box aria-hidden="true" sx={{ ...base, border: '1.5px dashed', borderColor: 'var(--text-secondary)' }} />;
  return <Box aria-hidden="true" sx={{ ...base, background: hatch(t.main), boxShadow: `inset 0 0 0 1px ${t.main}` }} />;
}

/** Fita S-T-Q-Q-S-S-D, a mesma de Agendas: cheio = gera horário; tracejado = há turno, mas não chega. */
function WeekStrip({ days, mode, tone = 'error' }) {
  const t = tint(mode === 'alert' ? tone : 'neutral');
  return (
    <Box component="ul" aria-label="Dias da semana" sx={{ ...LIST_RESET, display: 'flex', gap: 0.5, maxWidth: 252 }}>
      {DAY_INITIAL.map((d, i) => {
        const on = !!(days && days[i]);
        const state = on ? (mode === 'live' ? 'on' : 'planned') : 'off';
        const srText = state === 'on' ? 'tem horário para o cliente' : state === 'planned' ? 'tem turno, mas não gera horário' : 'sem atendimento';
        return (
          <Box
            component="li"
            key={i}
            aria-label={`${DAY_NAME[i]}: ${srText}`}
            sx={{
              flex: '1 1 0', minWidth: 0, height: 26, ml: i === 5 ? 0.5 : 0, borderRadius: '6px', boxSizing: 'border-box',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              ...(state === 'on' && { bgcolor: 'var(--color-primary-soft)', color: 'var(--color-primary-dark)' }),
              ...(state === 'planned' && { border: '1.5px dashed', borderColor: t.main, color: t.fg }),
              ...(state === 'off' && { bgcolor: 'var(--bg-page)', color: 'var(--text-disabled)' }),
            }}
          >
            <Typography variant="caption" aria-hidden="true" sx={{ fontWeight: state === 'off' ? 'var(--fw-regular)' : 'var(--fw-bold)' }}>{d}</Typography>
          </Box>
        );
      })}
    </Box>
  );
}

function EmptyNote({ icon, tone = 'neutral', children, action }) {
  const Icon = icon || InfoOutlined;
  const t = tint(tone);
  const calmo = tone === 'neutral' || tone === 'info';
  return (
    <Box
      sx={{
        display: 'flex', flexDirection: 'column', gap: 1.25, p: 1.75, borderRadius: 'var(--radius-sm)',
        border: '1px dashed', borderColor: calmo ? 'var(--divider)' : t.border, bgcolor: calmo ? 'transparent' : t.bg,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, color: calmo ? 'var(--text-secondary)' : t.fg }}>
        <Icon sx={{ fontSize: 20, flexShrink: 0 }} aria-hidden="true" />
        <Typography variant="body2" sx={{ color: 'inherit', textWrap: 'pretty' }}>{children}</Typography>
      </Box>
      {action && <Box>{action}</Box>}
    </Box>
  );
}

/** Pagamentos: os ícones por tipo (o tipo é dado de negócio; o desenho é nosso), com o nome no tooltip. */
function PayIcons({ items }) {
  const list = items || [];
  if (!list.length) return null;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
      {list.map((p, i) => {
        const Icon = payIcon(p.paymentType);
        return (
          <Tooltip key={`${p.name}-${i}`} title={p.name}>
            <Box sx={{ width: 24, height: 24, borderRadius: '6px', bgcolor: 'var(--bg-page)', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon sx={{ fontSize: 15 }} aria-hidden="true" />
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  );
}

/** "2 aguardando confirmação" (v2) — a pendência da Home, por lugar. É AÇÃO do dono (leva a Agendamentos),
 *  não defeito do lugar: por isso a cor de ação (primária suave), e não âmbar ou vermelho. */
const aguardando = (act) => (act && act.awaitingConfirmationCount > 0 && act.awaitingConfirmationLabel ? act.awaitingConfirmationLabel : null);

function AwaitingButton({ label, placeId, a }) {
  return (
    <ButtonBase
      onClick={() => a.onOpenAwaitingConfirmation?.(placeId)}
      sx={{
        alignSelf: 'flex-start', maxWidth: '100%', minHeight: 40, px: 1.25, gap: 0.75, borderRadius: 999,
        bgcolor: 'var(--color-primary-soft)', color: 'var(--color-primary-dark)',
        border: '1px solid', borderColor: 'color-mix(in srgb, var(--color-primary) 30%, transparent)',
        transition: 'border-color .15s', '&:hover': { borderColor: 'var(--color-primary)' },
        '&:hover .pl-seta': { transform: 'translateX(3px)' }, ...FOCUS,
      }}
    >
      <HourglassTopRounded sx={{ fontSize: 18, flexShrink: 0 }} aria-hidden="true" />
      <Typography variant="body2" component="span" sx={{ ...HEAD, color: 'inherit', textAlign: 'left' }}>
        {label}
        <Box component="span" sx={SR_ONLY}> — confirmar em Agendamentos</Box>
      </Typography>
      <ArrowForwardRounded className="pl-seta" sx={{ fontSize: 16, flexShrink: 0, transition: 'transform .2s', [REDUCED]: { transition: 'none' } }} aria-hidden="true" />
    </ButtonBase>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   O VEREDITO
   ═══════════════════════════════════════════════════════════════════════════ */

/** Peso ALTO: faixa de tint no topo do cartão (ou do lugar aberto). */
function AlertBand({ r, size = 'md', children }) {
  const t = tint(tomAlerta(r));
  const lg = size === 'lg';
  return (
    <Box sx={{ bgcolor: t.bg, borderBottom: '1px solid', borderColor: t.border, px: { xs: 2, sm: lg ? 3 : 2.25 }, py: lg ? 2 : 1.5, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        <Box sx={{ width: lg ? 44 : 36, height: lg ? 44 : 36, borderRadius: '50%', flexShrink: 0, bgcolor: 'var(--surface)', border: '1px solid', borderColor: t.border, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <EventBusyRounded sx={{ fontSize: lg ? 24 : 20, color: t.fg }} aria-hidden="true" />
        </Box>
        <Box sx={{ minWidth: 0, pt: '2px' }}>
          <Typography variant={lg ? 'subtitle1' : 'subtitle2'} component="p" sx={{ ...HEAD, color: t.fg, textWrap: 'pretty' }}>{r.headline}</Typography>
          {r.detail && <Typography variant="body2" sx={{ color: t.fg, textWrap: 'pretty' }}>{r.detail}</Typography>}
        </Box>
      </Box>
      {children}
    </Box>
  );
}

/** Pesos CALMO (recebe — com o "mas…" em âmbar quando só parte aparece) e SERENO (escolha do dono). */
function VerdictLine({ r, size = 'md' }) {
  const peso = pesoDe(r);
  const waiting = peso === 'waiting';
  const t = tint(waiting ? 'neutral' : 'success');
  const Icon = waiting ? PauseCircleRounded : CheckCircleRounded;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: t.fg }}>
        <Icon sx={{ fontSize: size === 'lg' ? 22 : 18, flexShrink: 0 }} aria-hidden="true" />
        <Typography variant={size === 'lg' ? 'subtitle1' : 'body2'} component="p" sx={{ ...HEAD, color: t.fg }}>{r.headline}</Typography>
      </Box>
      {r.detail && (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, color: peso === 'partial' ? 'var(--tint-warning-fg)' : 'var(--text-secondary)', pl: peso === 'partial' ? 0 : 3.25 }}>
          {peso === 'partial' && <WarningAmberRounded sx={{ fontSize: 17, mt: '1px', flexShrink: 0, mr: 0.5 }} aria-hidden="true" />}
          <Typography variant="body2" component="p" sx={{ color: 'inherit', textWrap: 'pretty' }}>{r.detail}</Typography>
        </Box>
      )}
    </Box>
  );
}

/** Uma pendência, com o porquê sob demanda e o conserto ali mesmo. */
function IssueRow({ issue, placeId, a, principal, open, onToggle }) {
  const tone = issueTone(issue);
  const t = tint(tone);
  const Icon = ISSUE_ICON[issue.isByChoice ? 'neutral' : issue.status] || InfoOutlined;
  const whyId = `pl-why-${placeId}-${issue.key}`;
  const temConserto = !!(issue.actionLabel && issue.fix);
  return (
    <Box component="li" sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start', py: 1.25 }}>
      <Box aria-hidden="true" sx={{ width: 28, height: 28, borderRadius: '50%', flexShrink: 0, bgcolor: t.bg, border: '1.5px solid', borderColor: tone === 'neutral' ? 'var(--divider)' : t.border, color: t.fg, display: 'flex', alignItems: 'center', justifyContent: 'center', mt: '2px' }}>
        <Icon sx={{ fontSize: 16 }} />
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', columnGap: 1.5, rowGap: 0.75 }}>
          <Box sx={{ minWidth: 0, flex: '1 1 200px', pt: '4px' }}>
            <Typography variant="subtitle2" component="p" sx={{ ...HEAD, color: 'var(--text-primary)', textWrap: 'pretty' }}>
              <Box component="span" sx={SR_ONLY}>{`${ISSUE_SR[tone] || ''}: `}</Box>
              {issue.label}
            </Typography>
            {issue.why && (
              <Button
                onClick={onToggle}
                aria-expanded={open}
                aria-controls={whyId}
                endIcon={<ExpandMoreRounded sx={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }} />}
                sx={{ ...QUIET_BTN_SX, minHeight: 40, px: 0.75, ml: -0.75, color: tone === 'neutral' ? 'var(--text-secondary)' : t.sub }}
              >
                Por que isso importa?
              </Button>
            )}
          </Box>
          {temConserto && (
            <Button
              onClick={() => a.onFix?.(placeId, issue.fix)}
              disableElevation
              endIcon={<ArrowForwardRounded sx={{ fontSize: 18, transition: 'transform .2s' }} />}
              sx={{ ...(principal ? PRIMARY_BTN_SX : OUTLINE_BTN_SX), minHeight: 40, whiteSpace: 'normal', textAlign: 'left', '&:hover .MuiButton-endIcon svg': { transform: 'translateX(3px)' } }}
            >
              {issue.actionLabel}
            </Button>
          )}
        </Box>
        <Collapse in={open} unmountOnExit onEntered={(node) => mostrar(node.querySelector(`[id="${whyId}"]`) || node)}>
          <Box id={whyId} sx={{ mt: 1, p: 1.5, borderRadius: 'var(--radius-sm)', bgcolor: 'var(--bg-page)', border: '1px solid', borderColor: 'var(--divider)', ...SCROLL_MARGIN }}>
            <Typography variant="body2" sx={{ color: 'var(--text-primary)', textWrap: 'pretty' }}>{issue.why}</Typography>
          </Box>
        </Collapse>
      </Box>
    </Box>
  );
}

function IssueList({ issues, placeId, a, title }) {
  const [openWhy, setOpenWhy] = useState(null); // UI: qual "por quê" está aberto
  const list = issues || [];
  if (!list.length) return null;
  // O "próximo passo" (botão cheio) é o primeiro conserto de algo que IMPEDE; os outros, contorno.
  const principal = (list.find((i) => !i.isByChoice && i.status === 'error' && i.fix && i.actionLabel) || {}).key;
  return (
    <Box sx={{ minWidth: 0 }}>
      {title && <Typography variant="overline" component="h3" sx={{ ...OVERLINE, display: 'block' }}>{title}</Typography>}
      <Box component="ul" sx={{ ...LIST_RESET, '& > li + li': { borderTop: '1px solid', borderColor: 'var(--divider)' } }}>
        {list.map((i) => (
          <IssueRow
            key={i.key}
            issue={i}
            placeId={placeId}
            a={a}
            principal={i.key === principal}
            open={openWhy === i.key}
            onToggle={() => setOpenWhy((k) => (k === i.key ? null : i.key))}
          />
        ))}
      </Box>
    </Box>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   O RESUMO DA CONTA (vários lugares)
   ═══════════════════════════════════════════════════════════════════════════ */

function Roster({ items, onJump }) {
  if (!items || items.length < 2) return null;
  return (
    <Box sx={{ minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '3px', height: 28 }}>
        {items.map((it) => {
          const r = it.readiness || {};
          const peso = pesoDe(r);
          const t = tint(tomAlerta(r));
          const bar = peso === 'live' || peso === 'partial'
            ? { height: 8, bgcolor: 'var(--color-success)' }
            : peso === 'waiting'
              ? { height: 8, border: '1.5px dashed', borderColor: 'var(--text-secondary)' }
              : { height: 16, background: hatch(t.main), boxShadow: `inset 0 0 0 1px ${t.main}` };
          return (
            <Tooltip key={it.id} title={`${it.name} — ${r.headline || ''}`}>
              <ButtonBase
                tabIndex={-1}
                aria-hidden="true"
                onClick={() => onJump(it.id)}
                sx={{ flex: '1 1 0', minWidth: 6, maxWidth: 72, height: 28, display: 'flex', alignItems: 'center', '&:hover > span': { transform: 'scaleY(1.35)' } }}
              >
                <Box component="span" sx={{ display: 'block', width: '100%', borderRadius: 3, boxSizing: 'border-box', transition: 'transform .15s', ...bar }} />
              </ButtonBase>
            </Tooltip>
          );
        })}
      </Box>
    </Box>
  );
}

function StatTile({ kind, tone, label, onClick }) {
  const t = tint(kind === 'alert' ? tone : 'success');
  const content = (
    <>
      <Swatch kind={kind} tone={tone} width={14} height={kind === 'alert' ? 14 : 8} />
      <Typography variant="body2" component="span" sx={{ ...HEAD, color: kind === 'alert' ? t.fg : 'var(--text-primary)', whiteSpace: 'nowrap' }}>{label}</Typography>
      {onClick && <ArrowForwardRounded sx={{ fontSize: 16, color: t.fg }} aria-hidden="true" />}
    </>
  );
  const sx = {
    display: 'inline-flex', alignItems: 'center', gap: 1, minHeight: 44, px: 1.5, py: 0.75,
    borderRadius: 'var(--radius-sm)', bgcolor: 'var(--surface)', border: '1px solid',
    borderColor: kind === 'alert' ? t.border : 'var(--divider)', maxWidth: '100%',
  };
  if (!onClick) return <Box sx={sx}>{content}</Box>;
  return (
    <ButtonBase
      onClick={onClick}
      aria-label={`${label}. Ir até ele.`}
      sx={{ ...sx, transition: 'border-color .15s, transform .15s', '&:hover': { borderColor: t.main, transform: 'translateY(-1px)' }, ...FOCUS, [REDUCED]: { '&:hover': { transform: 'none' } } }}
    >
      {content}
    </ButtonBase>
  );
}

function SummaryBand({ summary, items, onJump, firstAlert }) {
  const s = summary || {};
  const t = tint(s.status);
  const calm = !['error', 'warning'].includes(s.status);
  const vida = [s.futureAppointmentsLabel, s.nextAppointmentLabel].filter(Boolean).join(' · ');
  return (
    <Box
      component="section"
      aria-label="Situação dos seus lugares de atendimento"
      sx={{
        ...CARD_SX, ...rise(0),
        bgcolor: calm ? 'var(--surface)' : t.bg, borderColor: calm ? 'var(--divider)' : t.border,
        p: { xs: 2, sm: 2.25 }, mb: 'var(--space-3)', display: 'flex', flexDirection: 'column', gap: 1.5,
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'stretch', md: 'center' }, gap: { xs: 1.25, md: 2.5 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
          <StorefrontRounded sx={{ fontSize: 28, color: calm ? 'var(--color-primary-dark)' : t.fg, flexShrink: 0 }} aria-hidden="true" />
          <Typography variant="h6" component="p" sx={{ ...HEAD, color: 'var(--text-primary)', whiteSpace: 'nowrap' }}>{s.totalLabel}</Typography>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {s.bookableLabel && <StatTile kind="live" label={s.bookableLabel} />}
          {s.attentionCount > 0 && s.attentionLabel && (
            <StatTile
              kind="alert"
              tone={tomAlerta(firstAlert?.readiness)}
              label={s.attentionLabel}
              onClick={firstAlert ? () => onJump(firstAlert.id) : undefined}
            />
          )}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Roster items={items} onJump={onJump} />
        </Box>
      </Box>
      {(s.teamLabel || s.catalogLabel || vida || s.noAddressLabel) && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 2.5, rowGap: 0.75 }}>
          {s.teamLabel && <ContextLine icon={PeopleAltRounded}>{s.teamLabel}</ContextLine>}
          {s.catalogLabel && <ContextLine icon={DesignServicesRounded}>{s.catalogLabel}</ContextLine>}
          {vida && <ContextLine icon={EventAvailableRounded}>{vida}</ContextLine>}
          {s.noAddressLabel && <ContextLine icon={LocationOffRounded} tone="info">{s.noAddressLabel}</ContextLine>}
        </Box>
      )}
    </Box>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Filtros
   ═══════════════════════════════════════════════════════════════════════════ */

function FiltersBar({ filters, labels, a }) {
  const f = filters || {};
  const segs = f.segmentOptions || [];
  const result = f.resultLabel ? (
    <Typography variant="caption" role="status" sx={{ color: 'var(--text-secondary)', ...NUM, ml: { sm: 'auto' } }}>{f.resultLabel}</Typography>
  ) : null;
  return (
    <Box sx={{ ...CARD_SX, p: { xs: 1.5, sm: 1.75 }, mb: 'var(--space-3)', display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.25 }}>
        <TextField
          value={f.searchText || ''}
          onChange={(e) => a.onSearchChange?.(e.target.value)}
          placeholder={labels.searchPlaceholder}
          size="small"
          inputProps={{ 'aria-label': labels.searchPlaceholder }}
          InputProps={{ startAdornment: (<InputAdornment position="start"><SearchRounded sx={{ fontSize: 20, color: 'var(--text-secondary)' }} /></InputAdornment>) }}
          sx={{ flex: '2 1 260px', minWidth: 0, '& .MuiOutlinedInput-root': { borderRadius: 'var(--radius-sm)', minHeight: 42, bgcolor: 'var(--surface)' }, '& .MuiOutlinedInput-input': { minHeight: 24, py: '9px' } }}
        />
        {(f.sortOptions || []).length > 0 && (
          <Select
            value={f.sortOrder || ''}
            onChange={(e) => a.onSortChange?.(e.target.value)}
            size="small"
            inputProps={{ 'aria-label': 'Ordenar' }}
            startAdornment={<InputAdornment position="start"><SortRounded sx={{ fontSize: 20, color: 'var(--text-secondary)' }} /></InputAdornment>}
            sx={{ flex: '1 1 190px', minWidth: 0, minHeight: 42, borderRadius: 'var(--radius-sm)', bgcolor: 'var(--surface)', '& .MuiSelect-select': { minHeight: '24px !important', py: '9px', display: 'flex', alignItems: 'center' } }}
          >
            {f.sortOptions.map((o) => <MenuItem key={o.value} value={o.value} sx={{ minHeight: 40 }}>{o.label}</MenuItem>)}
          </Select>
        )}
        <Button onClick={() => a.onClearFilters?.()} disabled={!f.canClear} startIcon={<FilterAltOffRounded sx={{ fontSize: 18 }} />} sx={OUTLINE_BTN_SX}>
          Limpar
        </Button>
        {segs.length <= 1 && result}
      </Box>
      {segs.length > 1 && (
        <Box role="group" aria-label="Mostrar" sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75 }}>
          {segs.map((o) => {
            const on = f.segment === o.value;
            return (
              <ButtonBase
                key={o.value}
                aria-pressed={on}
                onClick={() => a.onSegmentChange?.(o.value)}
                sx={{
                  minHeight: 40, px: 1.5, gap: 0.75, borderRadius: 999, border: '1px solid',
                  borderColor: on ? 'var(--color-primary)' : 'var(--divider)',
                  bgcolor: on ? 'var(--color-primary-soft)' : 'var(--surface)',
                  color: on ? 'var(--color-primary-dark)' : 'var(--text-primary)',
                  transition: 'border-color .15s, background-color .15s',
                  '&:hover': { borderColor: 'var(--color-primary)' },
                  ...FOCUS,
                }}
              >
                {on && <CheckRounded sx={{ fontSize: 16 }} aria-hidden="true" />}
                <Typography variant="body2" component="span" sx={{ fontWeight: on ? 'var(--fw-bold)' : 'var(--fw-medium)', color: 'inherit', whiteSpace: 'nowrap' }}>{o.label}</Typography>
                <Box component="span" sx={{ minWidth: 22, height: 22, px: 0.75, borderRadius: 999, bgcolor: on ? 'var(--surface)' : 'var(--bg-page)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography variant="caption" component="span" sx={{ ...HEAD, ...NUM, color: 'inherit' }}>{o.count}</Typography>
                </Box>
              </ButtonBase>
            );
          })}
          {result}
        </Box>
      )}
    </Box>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   A LISTA
   ═══════════════════════════════════════════════════════════════════════════ */

function AddressShort({ address }) {
  if (!address || address.isMissing) return <ContextLine icon={LocationOffRounded}>Sem endereço</ContextLine>;
  return <ContextLine icon={PlaceRounded}>{address.shortLabel || address.line1}</ContextLine>;
}

const vidaDe = (act) => {
  const x = act || {};
  if (x.futureLabel) return [x.futureLabel, x.nextAppointmentLabel].filter(Boolean).join(' · ');
  return x.lastAppointmentLabel || x.emptyLabel || null;
};

function PlaceCard({ item, a, cardRef, openRef, flash, delay }) {
  const r = item.readiness || {};
  const peso = pesoDe(r);
  const t = tint(tomAlerta(r));
  const headingId = `pl-${item.id}`;
  const issues = r.issues || [];
  // Na faixa alta: o 1º conserto do que IMPEDE. A lista das pendências só aparece quando são duas ou mais —
  // com uma só, a frase do veredito já diz o mesmo. ("Sem endereço" fica de fora: já está no lugar do endereço.)
  const fortes = issues.filter((i) => !i.isByChoice && i.status !== 'info');
  const primeira = fortes.find((i) => i.fix && i.actionLabel);
  const team = item.team || {};
  const cat = item.catalog || {};
  const pay = item.payments || {};
  const vida = vidaDe(item.activity);
  const espera = aguardando(item.activity);
  const pres = item.presence || {};
  return (
    <Box
      component="article"
      ref={cardRef}
      tabIndex={-1}
      aria-labelledby={headingId}
      sx={{
        ...CARD_SX, ...rise(delay), overflow: 'hidden', display: 'flex', flexDirection: 'column', minWidth: 0,
        outline: 'none', ...SCROLL_MARGIN,
        borderColor: peso === 'alert' ? t.border : 'var(--divider)',
        transition: 'box-shadow .2s, border-color .2s',
        '&:hover': { boxShadow: 'var(--shadow-hover)', borderColor: peso === 'alert' ? t.main : 'var(--color-primary-light)' },
        ...(flash && { boxShadow: '0 0 0 3px var(--color-primary-light), var(--shadow-hover)' }),
      }}
    >
      {peso === 'alert' ? (
        <AlertBand r={r}>
          {fortes.length > 1 && (
            <Box component="ul" aria-label="O que falta" sx={{ ...LIST_RESET, display: 'flex', flexDirection: 'column', gap: 0.5, pl: { xs: 0, sm: 6.5 } }}>
              {fortes.map((i) => (
                <Box component="li" key={i.key} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, color: t.fg }}>
                  <Box aria-hidden="true" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: t.main, mt: '7px', flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ color: 'inherit', fontWeight: 'var(--fw-medium)' }}>{i.label}</Typography>
                </Box>
              ))}
            </Box>
          )}
          {primeira && (
            <Box sx={{ pl: { xs: 0, sm: 6.5 } }}>
              <Button
                onClick={() => a.onFix?.(item.id, primeira.fix)}
                disableElevation
                endIcon={<ArrowForwardRounded sx={{ fontSize: 18 }} />}
                sx={{ ...PRIMARY_BTN_SX, minHeight: 40, whiteSpace: 'normal', textAlign: 'left' }}
              >
                {primeira.actionLabel}
              </Button>
            </Box>
          )}
        </AlertBand>
      ) : (
        <Box sx={{ px: { xs: 2, sm: 2.25 }, pt: { xs: 2, sm: 2.25 } }}>
          <VerdictLine r={r} />
        </Box>
      )}

      <Box sx={{ p: { xs: 2, sm: 2.25 }, display: 'flex', flexDirection: 'column', gap: 1.5, flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, minWidth: 0 }}>
          <Monogram initials={item.initials} size={48} />
          <Box sx={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            <Typography id={headingId} variant="subtitle1" component="h3" sx={{ ...HEAD, ...BREAK, color: 'var(--text-primary)', lineHeight: 1.3 }}>{item.name}</Typography>
            <AddressShort address={item.address} />
            {item.sinceLabel && <ContextLine icon={AccessTimeRounded}>{item.sinceLabel}</ContextLine>}
          </Box>
        </Box>
        {item.description && (
          <Typography variant="body2" sx={{ color: 'var(--text-secondary)', textWrap: 'pretty' }}>{item.description}</Typography>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pt: 1.25, borderTop: '1px solid', borderColor: 'var(--divider)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            {team.count > 0 ? <Faces members={team.members} moreLabel={team.moreLabel} size={28} /> : <PersonOffRounded sx={{ fontSize: 20, color: 'var(--text-secondary)' }} aria-hidden="true" />}
            <Typography variant="body2" sx={{ color: 'var(--text-primary)', fontWeight: 'var(--fw-medium)', minWidth: 0, ...BREAK }}>{team.label}</Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            <ContextLine icon={DesignServicesRounded}>{cat.label}</ContextLine>
            {cat.withoutProfessionalLabel && <ContextLine icon={WarningAmberRounded} tone="warning">{cat.withoutProfessionalLabel}</ContextLine>}
          </Box>
          {pay.isConfigured && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flexWrap: 'wrap' }}>
              <PayIcons items={pay.items} />
              <Typography variant="caption" sx={{ color: 'var(--text-secondary)', ...BREAK }}>{pay.label}</Typography>
            </Box>
          )}
        </Box>

        {(vida || espera || pres.label || item.absentLabel) && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 'auto' }}>
            {vida && <ContextLine icon={EventAvailableRounded}>{vida}</ContextLine>}
            {espera && <Box sx={{ display: 'flex', py: 0.25 }}><AwaitingButton label={espera} placeId={item.id} a={a} /></Box>}
            {pres.label && <ContextLine icon={LanguageRounded}>{pres.label}</ContextLine>}
            {item.absentLabel && <ContextLine icon={BeachAccessRounded} tone="info">{item.absentLabel}</ContextLine>}
          </Box>
        )}
      </Box>

      <Box sx={{ px: { xs: 1.25, sm: 1.5 }, py: 0.75, borderTop: '1px solid', borderColor: 'var(--divider)', display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Button
          ref={openRef}
          onClick={() => a.onOpen?.(item.id)}
          endIcon={<ArrowForwardRounded sx={{ fontSize: 18 }} />}
          aria-label={`Ver detalhes de ${item.name}`}
          sx={{ ...LINK_BTN_SX, ...SCROLL_MARGIN }}
        >
          Ver detalhes
        </Button>
        <Box sx={{ flex: 1 }} />
        {item.address?.mapsUrl && (
          <Tooltip title="Ver no mapa">
            <IconButton component="a" href={item.address.mapsUrl} target="_blank" rel="noopener noreferrer" aria-label={`Ver ${item.name} no mapa (abre em outra aba)`} sx={ICON_BTN_SX}>
              <MapRounded sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Editar">
          <IconButton aria-label={`Editar ${item.name}`} onClick={() => a.onEdit?.(item.id)} sx={ICON_BTN_SX}>
            <EditRounded sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Excluir">
          <IconButton aria-label={`Excluir ${item.name}`} onClick={() => a.onDelete?.(item.id)} sx={DANGER_ICON_BTN_SX}>
            <DeleteOutlineRounded sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

const CARDS_GRID = {
  display: 'grid', gap: 'var(--space-2)', alignItems: 'stretch',
  gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 340px), 1fr))',
};

/* ── Um lugar só: o RETRATO ─────────────────────────────────────────────── */
function PortraitFact({ icon, title, children }) {
  const Icon = icon;
  return (
    <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      <Box component="dt" sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'var(--text-secondary)' }}>
        <Icon sx={{ fontSize: 18 }} aria-hidden="true" />
        <Typography variant="overline" component="span" sx={OVERLINE}>{title}</Typography>
      </Box>
      <Box component="dd" sx={{ m: 0, display: 'flex', flexDirection: 'column', gap: 0.75, minWidth: 0 }}>{children}</Box>
    </Box>
  );
}

function Portrait({ item, a, cardRef, openRef }) {
  const r = item.readiness || {};
  const peso = pesoDe(r);
  const headingId = `pl-${item.id}`;
  const team = item.team || {};
  const cat = item.catalog || {};
  const pay = item.payments || {};
  const act = item.activity || {};
  const pres = item.presence || {};
  return (
    <Box
      component="article"
      ref={cardRef}
      tabIndex={-1}
      aria-labelledby={headingId}
      sx={{ ...CARD_SX, ...rise(0), overflow: 'hidden', outline: 'none', containerType: 'inline-size', containerName: 'retrato', ...SCROLL_MARGIN }}
    >
      {peso === 'alert'
        ? <AlertBand r={r} size="lg" />
        : <Box sx={{ px: { xs: 2, sm: 3 }, pt: { xs: 2, sm: 2.5 } }}><VerdictLine r={r} size="lg" /></Box>}

      <Box sx={{ p: { xs: 2, sm: 3 }, display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Monogram initials={item.initials} size={64} />
          <Box sx={{ minWidth: 0, flex: '1 1 240px', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography id={headingId} variant="h5" component="h2" sx={{ ...HEAD, ...BREAK, color: 'var(--text-primary)', textWrap: 'pretty' }}>{item.name}</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 2, rowGap: 0.25 }}>
              <AddressShort address={item.address} />
              {item.sinceLabel && <ContextLine icon={AccessTimeRounded}>{item.sinceLabel}</ContextLine>}
            </Box>
            {item.description && <Typography variant="body2" sx={{ color: 'var(--text-secondary)', textWrap: 'pretty' }}>{item.description}</Typography>}
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', width: { xs: '100%', sm: 'auto' } }}>
            <Button
              ref={openRef}
              onClick={() => a.onOpen?.(item.id)}
              disableElevation
              endIcon={<ArrowForwardRounded />}
              aria-label={`Ver equipe, serviços e preços de ${item.name}`}
              sx={{ ...PRIMARY_BTN_SX, ...SCROLL_MARGIN, flex: { xs: 1, sm: '0 0 auto' } }}
            >
              Ver equipe, serviços e preços
            </Button>
            <Tooltip title="Editar">
              <IconButton aria-label={`Editar ${item.name}`} onClick={() => a.onEdit?.(item.id)} sx={ICON_BTN_SX}>
                <EditRounded sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Box
          component="dl"
          sx={{
            m: 0, display: 'grid', gap: 2.5, gridTemplateColumns: 'minmax(0, 1fr)',
            pt: 2.5, borderTop: '1px solid', borderColor: 'var(--divider)',
            '@container retrato (min-width: 560px)': { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' },
            '@container retrato (min-width: 940px)': { gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', columnGap: 3 },
          }}
        >
          <PortraitFact icon={PeopleAltRounded} title="Quem atende">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {team.count > 0 && <Faces members={team.members} moreLabel={team.moreLabel} size={32} />}
              <Typography variant="body2" sx={{ ...HEAD, color: 'var(--text-primary)' }}>{team.label}</Typography>
            </Box>
          </PortraitFact>
          <PortraitFact icon={DesignServicesRounded} title="O que se oferece">
            <Typography variant="body2" sx={{ ...HEAD, color: 'var(--text-primary)' }}>{cat.label}</Typography>
            {cat.withoutProfessionalLabel && <ContextLine icon={WarningAmberRounded} tone="warning">{cat.withoutProfessionalLabel}</ContextLine>}
          </PortraitFact>
          <PortraitFact icon={PaymentsRounded} title="Como pagar">
            {pay.isConfigured ? (
              <>
                <PayIcons items={pay.items} />
                <Typography variant="body2" sx={{ color: 'var(--text-primary)' }}>{pay.label}</Typography>
              </>
            ) : (
              <Typography variant="body2" sx={{ color: 'var(--text-secondary)', textWrap: 'pretty' }}>{pay.emptyLabel}</Typography>
            )}
          </PortraitFact>
          <PortraitFact icon={InsightsRounded} title="Movimento">
            {act.futureLabel && <Typography variant="body2" sx={{ ...HEAD, color: 'var(--text-primary)' }}>{act.futureLabel}</Typography>}
            {act.nextAppointmentLabel && <ContextLine icon={AccessTimeRounded}>{act.nextAppointmentLabel}</ContextLine>}
            {aguardando(act) && <AwaitingButton label={aguardando(act)} placeId={item.id} a={a} />}
            {act.last30DaysLabel && <ContextLine icon={InsightsRounded}>{act.last30DaysLabel}</ContextLine>}
            {act.onlineShareLabel && <ContextLine icon={LanguageRounded}>{act.onlineShareLabel}</ContextLine>}
            {!act.futureLabel && (act.lastAppointmentLabel || act.emptyLabel) && (
              <Typography variant="body2" sx={{ color: 'var(--text-secondary)', textWrap: 'pretty' }}>{act.lastAppointmentLabel || act.emptyLabel}</Typography>
            )}
            {pres.label && <ContextLine icon={PublicRounded}>{pres.label}</ContextLine>}
          </PortraitFact>
        </Box>

        {item.absentLabel && <ContextLine icon={BeachAccessRounded} tone="info">{item.absentLabel}</ContextLine>}
        {(r.issues || []).length > 0 && (
          <Box sx={{ pt: 1, borderTop: '1px solid', borderColor: 'var(--divider)' }}>
            <IssueList issues={r.issues} placeId={item.id} a={a} title={tituloPendencias(r)} />
          </Box>
        )}
      </Box>
    </Box>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   O LUGAR ABERTO
   ═══════════════════════════════════════════════════════════════════════════ */

function Section({ id, icon, title, subtitle, action, container, children }) {
  const Icon = icon;
  return (
    <Box
      component="section"
      aria-labelledby={id}
      sx={{
        ...CARD_SX, p: { xs: 2, sm: 2.25 }, display: 'flex', flexDirection: 'column', gap: 1.75, minWidth: 0,
        ...(container && { containerType: 'inline-size', containerName: container }),
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, minWidth: 0, flex: '1 1 200px' }}>
          <Icon sx={{ fontSize: 20, color: 'var(--color-primary-dark)', mt: '2px', flexShrink: 0 }} aria-hidden="true" />
          <Box sx={{ minWidth: 0 }}>
            <Typography id={id} variant="subtitle1" component="h3" sx={{ ...HEAD, color: 'var(--text-primary)' }}>{title}</Typography>
            {subtitle && <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>{subtitle}</Typography>}
          </Box>
        </Box>
        {action}
      </Box>
      {children}
    </Box>
  );
}

/** Voltar e trocar de lugar sem passar pela lista. */
function DetailNav({ items, selectedId, labels, a }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 'var(--space-2)' }}>
      <Button onClick={() => a.onCloseDetail?.()} startIcon={<ArrowBackRounded />} sx={{ ...OUTLINE_BTN_SX, color: 'var(--text-primary)' }}>
        {labels.title}
      </Button>
      <Box sx={{ flex: 1 }} />
      <Box component="nav" aria-label={labels.title} sx={{ display: { xs: 'none', sm: 'flex' }, flexWrap: 'wrap', gap: 0.75, justifyContent: 'flex-end', minWidth: 0 }}>
        {items.map((it) => {
          const atual = it.id === selectedId;
          const peso = pesoDe(it.readiness);
          return (
            <ButtonBase
              key={it.id}
              aria-current={atual ? 'page' : undefined}
              onClick={atual ? undefined : () => a.onOpen?.(it.id)}
              sx={{
                minHeight: 40, maxWidth: 240, px: 1.25, gap: 0.75, borderRadius: 999, border: '1px solid',
                borderColor: atual ? 'var(--color-primary)' : 'var(--divider)',
                bgcolor: atual ? 'var(--color-primary-soft)' : 'var(--surface)',
                color: atual ? 'var(--color-primary-dark)' : 'var(--text-primary)',
                cursor: atual ? 'default' : 'pointer', '&:hover': atual ? {} : { borderColor: 'var(--color-primary)' }, ...FOCUS,
              }}
            >
              <Swatch kind={peso} tone={tomAlerta(it.readiness)} width={10} height={peso === 'alert' ? 12 : 8} />
              <Typography variant="body2" component="span" sx={{ ...ONE_LINE, fontWeight: atual ? 'var(--fw-bold)' : 'var(--fw-medium)', color: 'inherit' }}>{it.name}</Typography>
              <Box component="span" sx={SR_ONLY}>{`— ${it.readiness?.headline || ''}`}</Box>
            </ButtonBase>
          );
        })}
      </Box>
      <Select
        value={selectedId || ''}
        onChange={(e) => { if (e.target.value !== selectedId) a.onOpen?.(e.target.value); }}
        size="small"
        inputProps={{ 'aria-label': labels.title }}
        sx={{ display: { xs: 'flex', sm: 'none' }, flex: '1 1 160px', minWidth: 0, minHeight: 42, borderRadius: 'var(--radius-sm)', bgcolor: 'var(--surface)', '& .MuiSelect-select': { minHeight: '24px !important', py: '9px', display: 'flex', alignItems: 'center' } }}
      >
        {items.map((it) => <MenuItem key={it.id} value={it.id} sx={{ minHeight: 44 }}>{it.name}</MenuItem>)}
      </Select>
    </Box>
  );
}

function ContactBlock({ place, a }) {
  const ad = place.address || {};
  const c = place.contact || {};
  const links = [
    c.phoneHref && { key: 'tel', Icon: CallRounded, label: c.phoneLabel, href: c.phoneHref },
    c.whatsappHref && { key: 'wa', Icon: WhatsApp, label: 'WhatsApp', href: c.whatsappHref, ext: true },
    c.emailHref && { key: 'mail', Icon: MailOutlineRounded, label: c.email, href: c.emailHref },
    c.websiteHref && { key: 'web', Icon: LanguageRounded, label: c.websiteLabel, href: c.websiteHref, ext: true },
  ].filter(Boolean);
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 0 }}>
      {ad.isMissing ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'var(--text-secondary)' }}>
          <LocationOffRounded sx={{ fontSize: 20 }} aria-hidden="true" />
          <Typography variant="body2" sx={{ color: 'inherit' }}>Sem endereço</Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start', minWidth: 0 }}>
          <PlaceRounded sx={{ fontSize: 20, color: 'var(--color-primary-dark)', mt: '2px', flexShrink: 0 }} aria-hidden="true" />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ ...HEAD, color: 'var(--text-primary)', ...BREAK }}>{ad.line1}</Typography>
            {ad.line2 && <Typography variant="caption" sx={{ color: 'var(--text-secondary)', ...BREAK }}>{ad.line2}</Typography>}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.25, mt: 0.5, ml: -1.25 }}>
              {ad.mapsUrl && (
                <Button href={ad.mapsUrl} target="_blank" rel="noopener noreferrer" startIcon={<MapRounded sx={{ fontSize: 18 }} />} sx={LINK_BTN_SX}>
                  Ver no mapa<Box component="span" sx={SR_ONLY}> (abre em outra aba)</Box>
                </Button>
              )}
              <Button onClick={() => a.onCopyAddress?.(place.id)} startIcon={<ContentCopyRounded sx={{ fontSize: 18 }} />} sx={QUIET_BTN_SX}>
                Copiar endereço
              </Button>
            </Box>
          </Box>
        </Box>
      )}
      {links.length > 0 && (
        <Box component="ul" aria-label="Contato" sx={{ ...LIST_RESET, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          {links.map(({ key, Icon, label, href, ext }) => (
            <Box component="li" key={key} sx={{ minWidth: 0, maxWidth: '100%' }}>
              <Button
                href={href}
                {...(ext ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                startIcon={<Icon sx={{ fontSize: 18 }} />}
                sx={{ ...OUTLINE_BTN_SX, color: 'var(--text-primary)', maxWidth: '100%', '& .MuiButton-startIcon': { color: 'var(--color-primary-dark)' } }}
              >
                <Box component="span" sx={ONE_LINE}>{label}</Box>
                {ext && <Box component="span" sx={SR_ONLY}> (abre em outra aba)</Box>}
              </Button>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

function Completeness({ detail, a }) {
  if (!detail.completenessLabel) return null;
  return (
    <Box sx={{ p: 1.75, borderRadius: 'var(--radius-sm)', border: '1px dashed', borderColor: 'var(--color-primary-light)', bgcolor: 'var(--color-primary-soft)', display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', color: 'var(--color-primary-dark)' }}>
        <AutoAwesomeRounded sx={{ fontSize: 20 }} aria-hidden="true" />
        <Typography variant="subtitle2" component="p" sx={{ ...HEAD, color: 'var(--text-primary)' }}>{detail.completenessLabel}</Typography>
      </Box>
      <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
        Complete quando quiser: cada item abre a edição já no campo certo.
      </Typography>
      {(detail.missingFields || []).length > 0 && (
        <Box component="ul" sx={{ ...LIST_RESET, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          {detail.missingFields.map((m) => (
            <Box component="li" key={m.key}>
              <Button onClick={() => a.onCompletePlace?.(detail.id, m.key)} startIcon={<AddRounded sx={{ fontSize: 18 }} />} sx={{ ...OUTLINE_BTN_SX, color: 'var(--text-primary)' }}>
                {m.label}
              </Button>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

function DetailHero({ detail, a, headRef }) {
  const card = detail.card || {};
  const r = card.readiness || {};
  const peso = pesoDe(r);
  const issues = r.issues || [];
  // À direita vai o que pede ação (pendências e o convite para completar). Sem nada disso, o contato
  // ocupa esse lado — o veredito calmo no topo já diz que está tudo em ordem.
  const pendente = issues.length > 0 || !!detail.completenessLabel;
  return (
    <Box sx={{ ...CARD_SX, overflow: 'hidden', containerType: 'inline-size', containerName: 'heroi', mb: 'var(--space-2)' }}>
      {peso === 'alert'
        ? <AlertBand r={r} size="lg" />
        : <Box sx={{ px: { xs: 2, sm: 3 }, pt: { xs: 2, sm: 2.5 } }}><VerdictLine r={r} size="lg" /></Box>}
      {/* Três áreas: quem é, o lado (pendências — ou o contato, se nada pende) e as ações. Estreito: uma
          embaixo da outra, com as ações no fim; largo: o lado ocupa a coluna da direita inteira. */}
      <Box
        sx={{
          p: { xs: 2, sm: 3 }, display: 'grid', columnGap: 4, rowGap: 2.5, alignItems: 'start',
          gridTemplateColumns: 'minmax(0, 1fr)', gridTemplateAreas: '"quem" "lado" "acoes"',
          '@container heroi (min-width: 820px)': {
            gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.15fr)', gridTemplateRows: 'auto 1fr',
            gridTemplateAreas: '"quem lado" "acoes lado"',
          },
        }}
      >
        <Box sx={{ gridArea: 'quem', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, minWidth: 0 }}>
            <Monogram initials={card.initials} size={64} />
            <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.5, pt: 0.5 }}>
              <Typography
                ref={headRef}
                tabIndex={-1}
                id="pl-aberto-titulo"
                variant="h5"
                component="h2"
                sx={{ ...HEAD, ...BREAK, ...FOCUS_TITLE_SX, ...SCROLL_MARGIN, color: 'var(--text-primary)', textWrap: 'pretty', width: 'fit-content', maxWidth: '100%' }}
              >
                {card.name}
              </Typography>
              {card.sinceLabel && <ContextLine icon={AccessTimeRounded}>{card.sinceLabel}</ContextLine>}
            </Box>
          </Box>
          {card.description && <Typography variant="body2" sx={{ color: 'var(--text-secondary)', textWrap: 'pretty' }}>{card.description}</Typography>}
          {pendente && <ContactBlock place={card} a={a} />}
        </Box>

        <Box sx={{ gridArea: 'lado', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {pendente ? (
            <>
              <IssueList issues={issues} placeId={card.id} a={a} title={tituloPendencias(r)} />
              <Completeness detail={detail} a={a} />
            </>
          ) : (
            <ContactBlock place={card} a={a} />
          )}
        </Box>

        <Box sx={{ gridArea: 'acoes', minWidth: 0, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
          {aguardando(card.activity) && (
            <Box sx={{ flexBasis: '100%', display: 'flex' }}>
              <AwaitingButton label={aguardando(card.activity)} placeId={card.id} a={a} />
            </Box>
          )}
          {r.isBookable && (
            <Button onClick={() => a.onBook?.(card.id)} disableElevation startIcon={<EventAvailableRounded />} sx={PRIMARY_BTN_SX}>
              Agendar aqui
            </Button>
          )}
          <Button onClick={() => a.onEdit?.(card.id)} startIcon={<EditRounded sx={{ fontSize: 18 }} />} sx={{ ...OUTLINE_BTN_SX, color: 'var(--text-primary)' }}>
            Editar
          </Button>
          <Tooltip title="Excluir">
            <IconButton aria-label={`Excluir ${card.name}`} onClick={() => a.onDelete?.(card.id)} sx={DANGER_ICON_BTN_SX}>
              <DeleteOutlineRounded sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
}

/* ── Quem atende aqui ───────────────────────────────────────────────────── */
function MemberRow({ m, placeId, a }) {
  const r = m.readiness || {};
  const peso = pesoDe(r);
  const t = tint(tomDoPeso(r));
  const VIcon = peso === 'alert' ? EventBusyRounded : peso === 'partial' ? WarningAmberRounded : peso === 'waiting' ? PauseCircleRounded : CheckCircleRounded;
  return (
    <Box component="li" sx={{ p: 1.5, borderRadius: 'var(--radius-sm)', border: '1px solid', borderColor: 'var(--divider)', display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
      <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start', minWidth: 0 }}>
        <Avatar photoUrl={m.photoUrl} initials={m.initials} size={44} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="subtitle2" component="p" sx={{ ...HEAD, ...BREAK, color: 'var(--text-primary)' }}>{m.name}</Typography>
          <Typography variant="caption" sx={{ color: 'var(--text-secondary)', ...BREAK }}>
            {[m.specialty, m.servicesHereLabel].filter(Boolean).join(' · ')}
          </Typography>
          {m.isReceived && (
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mt: 0.5, px: 0.75, py: '2px', borderRadius: 999, bgcolor: 'var(--tint-info-bg)', color: 'var(--tint-info-fg)' }}>
              <HandshakeRounded sx={{ fontSize: 14 }} aria-hidden="true" />
              <Typography variant="caption" sx={{ color: 'inherit', fontWeight: 'var(--fw-medium)' }}>
                {m.ownerName ? `Outra conta · ${m.ownerName}` : 'Outra conta'}
              </Typography>
            </Box>
          )}
        </Box>
        <Tooltip title="Ver em Profissionais">
          <IconButton aria-label={`Ver ${m.name} em Profissionais`} onClick={() => a.onOpenProfessional?.(m.professionalId)} sx={{ ...ICON_BTN_SX, mt: -0.5, mr: -0.5 }}>
            <ChevronRightRounded />
          </IconButton>
        </Tooltip>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, color: t.fg }}>
        <VIcon sx={{ fontSize: 16, mt: '2px', flexShrink: 0 }} aria-hidden="true" />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" component="p" sx={{ ...HEAD, color: 'inherit' }}>{r.headline}</Typography>
          {r.detail && <Typography variant="caption" component="p" sx={{ color: peso === 'partial' ? 'var(--tint-warning-fg)' : 'var(--text-secondary)', textWrap: 'pretty' }}>{r.detail}</Typography>}
        </Box>
      </Box>
      {m.absence && (
        <ContextLine icon={BeachAccessRounded} tone={m.absence.status === 'warning' || m.absence.isNow ? 'warning' : 'info'}>
          {[m.absence.label, m.absence.reason].filter(Boolean).join(' · ')}
        </ContextLine>
      )}
      {m.activityLabel && <ContextLine icon={EventAvailableRounded}>{m.activityLabel}</ContextLine>}
      {(m.serviceNames || []).length > 0 && (
        <Typography variant="caption" component="p" sx={{ color: 'var(--text-secondary)', ...BREAK }}>{m.serviceNames.join(' · ')}</Typography>
      )}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, ml: -1.25 }}>
        {m.canConfigure ? (
          <Button onClick={() => a.onConfigureProfessionalServices?.(placeId, m.professionalId)} startIcon={<TuneRounded sx={{ fontSize: 18 }} />} sx={QUIET_BTN_SX}>
            Serviços aqui
          </Button>
        ) : (
          <Tooltip title="Você não pode mudar os serviços desta pessoa aqui.">
            <Box component="span" sx={{ display: 'inline-flex' }}>
              <Button disabled startIcon={<TuneRounded sx={{ fontSize: 18 }} />} sx={QUIET_BTN_SX}>Serviços aqui</Button>
            </Box>
          </Tooltip>
        )}
        {r.isBookable && (
          <Button onClick={() => a.onBook?.(placeId, m.professionalId)} startIcon={<EventAvailableRounded sx={{ fontSize: 18 }} />} sx={LINK_BTN_SX}>
            Agendar
          </Button>
        )}
      </Box>
    </Box>
  );
}

function TeamSection({ detail, a }) {
  const team = detail.team || [];
  return (
    <Section
      id="pl-sec-equipe"
      icon={PeopleAltRounded}
      title="Quem atende aqui"
      subtitle={detail.card?.team?.label}
      action={<Button onClick={() => a.onManageTeam?.(detail.id)} startIcon={<TuneRounded sx={{ fontSize: 18 }} />} sx={OUTLINE_BTN_SX}>Equipe</Button>}
    >
      {team.length === 0 ? (
        <EmptyNote icon={PersonOffRounded} tone="error">{detail.teamEmptyLabel}</EmptyNote>
      ) : (
        <Box component="ul" sx={{ ...LIST_RESET, display: 'grid', gap: 1, gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 260px), 1fr))' }}>
          {team.map((m) => <MemberRow key={m.professionalId} m={m} placeId={detail.id} a={a} />)}
        </Box>
      )}
    </Section>
  );
}

/* ── Quem faz o quê aqui (serviço × profissional) ──────────────────────── */
function ServiceStatus({ it, placeId, a }) {
  if (it.isBookable) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'var(--tint-success-fg)', minWidth: 0 }}>
          <CheckCircleRounded sx={{ fontSize: 16, flexShrink: 0 }} aria-hidden="true" />
          <Typography variant="caption" sx={{ color: 'inherit', fontWeight: 'var(--fw-medium)' }}>Dá para agendar</Typography>
        </Box>
        <Tooltip title="Agendar este serviço">
          <IconButton aria-label={`Agendar ${it.name}`} onClick={() => a.onBook?.(placeId, undefined, it.serviceId)} sx={ICON_BTN_SX}>
            <EventAvailableRounded sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
      </Box>
    );
  }
  // Defeito (warning/error) → tinta e negrito; pausa do dono (neutral) → calmo, com o ícone de pausa.
  const tom = pesoServico(it);
  const t = tint(tom);
  const Icon = iconeServico(it);
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, color: t.fg }}>
      <Icon sx={{ fontSize: 16, mt: '1px', flexShrink: 0 }} aria-hidden="true" />
      <Typography variant="caption" sx={{ color: 'inherit', fontWeight: ehDefeito(tom) ? 'var(--fw-bold)' : 'var(--fw-regular)', textWrap: 'pretty' }}>
        <Box component="span" sx={SR_ONLY}>{`${ISSUE_SR[tom] || ''}: `}</Box>
        {it.notBookableReason}
      </Typography>
    </Box>
  );
}

function ServiceName({ it }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="body2" component="span" sx={{ ...HEAD, ...BREAK, color: 'var(--text-primary)', display: 'block' }}>{it.name}</Typography>
      <Typography variant="caption" sx={{ color: 'var(--text-secondary)', ...NUM }}>
        {[it.durationLabel, it.basePriceLabel].filter(Boolean).join(' · ')}
      </Typography>
    </Box>
  );
}

const TH_SX = { textAlign: 'left', fontWeight: 'inherit', p: 0, pb: 1, verticalAlign: 'bottom', borderBottom: '1px solid', borderColor: 'var(--divider)' };
const TD_SX = { p: 0, py: 1, px: 0.5, verticalAlign: 'middle', borderBottom: '1px solid', borderColor: 'var(--divider)' };

function WhoDoesWhatTable({ groups, team, placeId, a }) {
  return (
    <Box component="table" sx={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, tableLayout: 'fixed' }}>
      <Box component="caption" sx={SR_ONLY}>Quem faz cada serviço aqui</Box>
      <Box component="colgroup">
        <Box component="col" sx={{ width: '42%' }} />
        {team.map((m) => <Box component="col" key={m.professionalId} sx={{ width: 48 }} />)}
        <Box component="col" />
      </Box>
      <Box component="thead">
        <Box component="tr">
          <Box component="th" scope="col" sx={TH_SX}><Typography variant="overline" sx={OVERLINE}>Serviço</Typography></Box>
          {team.map((m) => (
            <Box component="th" scope="col" key={m.professionalId} sx={{ ...TH_SX, textAlign: 'center' }}>
              <Tooltip title={m.name}>
                <Box sx={{ display: 'inline-flex' }}><Avatar photoUrl={m.photoUrl} initials={m.initials} size={30} /></Box>
              </Tooltip>
              <Box component="span" sx={SR_ONLY}>{m.name}</Box>
            </Box>
          ))}
          <Box component="th" scope="col" sx={{ ...TH_SX, pl: 1 }}><Typography variant="overline" sx={OVERLINE}>Para o cliente</Typography></Box>
        </Box>
      </Box>
      <Box component="tbody">
        {groups.map((g) => (
          <Fragment key={g.category}>
            <Box component="tr">
              <Box component="th" scope="colgroup" colSpan={team.length + 2} sx={{ ...TH_SX, pt: 1.75, pb: 0.5 }}>
                <Typography variant="caption" sx={{ ...HEAD, color: 'var(--text-secondary)' }}>{g.category}</Typography>
              </Box>
            </Box>
            {(g.items || []).map((it) => {
              const tom = pesoServico(it);
              const defeito = ehDefeito(tom);
              const ninguem = defeito && it.hasNoProfessional;
              const tt = tint(tom);
              const fazem = new Set((it.professionals || []).map((p) => p.id));
              const rowBg = defeito ? { bgcolor: tt.bg } : {};
              return (
                <Box component="tr" key={it.serviceId}>
                  <Box component="th" scope="row" sx={{ ...TD_SX, ...rowBg, textAlign: 'left', fontWeight: 'inherit', pl: defeito ? 1 : 0, borderLeft: defeito ? '3px solid' : 'none', borderLeftColor: tt.main }}>
                    <ServiceName it={it} />
                  </Box>
                  {team.map((m) => {
                    const faz = fazem.has(m.professionalId);
                    return (
                      <Box component="td" key={m.professionalId} sx={{ ...TD_SX, ...rowBg, textAlign: 'center' }}>
                        {faz ? (
                          <CheckCircleRounded sx={{ fontSize: 20, color: 'var(--color-primary-dark)' }} aria-label={`${m.name} faz`} />
                        ) : ninguem ? (
                          <Box aria-hidden="true" sx={{ width: 18, height: 18, mx: 'auto', borderRadius: '50%', background: hatch(tt.main), opacity: 0.55 }} />
                        ) : (
                          <Box aria-hidden="true" sx={{ width: 6, height: 6, mx: 'auto', borderRadius: '50%', bgcolor: 'var(--divider)' }} />
                        )}
                        {!faz && <Box component="span" sx={SR_ONLY}>{`${m.name} não faz`}</Box>}
                      </Box>
                    );
                  })}
                  <Box component="td" sx={{ ...TD_SX, ...rowBg, pl: 1 }}>
                    <ServiceStatus it={it} placeId={placeId} a={a} />
                  </Box>
                </Box>
              );
            })}
          </Fragment>
        ))}
      </Box>
    </Box>
  );
}

function ServiceList({ groups, placeId, a }) {
  return (
    <Box component="ul" sx={{ ...LIST_RESET, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {groups.map((g) => (
        <Box component="li" key={g.category}>
          <Typography variant="caption" component="p" sx={{ ...HEAD, color: 'var(--text-secondary)', mb: 0.5 }}>{g.category}</Typography>
          <Box component="ul" sx={{ ...LIST_RESET, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {(g.items || []).map((it) => {
              const tom = pesoServico(it);
              const defeito = ehDefeito(tom);
              const tt = tint(tom);
              return (
                <Box
                  component="li"
                  key={it.serviceId}
                  sx={{
                    p: 1.25, borderRadius: 'var(--radius-sm)', border: '1px solid', display: 'flex', flexDirection: 'column', gap: 0.75,
                    borderColor: defeito ? tt.border : 'var(--divider)', bgcolor: defeito ? tt.bg : 'transparent',
                    ...(defeito && { borderLeft: '3px solid', borderLeftColor: tt.main }),
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                    <ServiceName it={it} />
                    {(it.professionals || []).length > 0 && <Faces members={it.professionals} size={28} />}
                  </Box>
                  <ServiceStatus it={it} placeId={placeId} a={a} />
                </Box>
              );
            })}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

function ServicesSection({ detail, a }) {
  const groups = detail.catalog || [];
  const team = detail.team || [];
  const cat = detail.card?.catalog || {};
  const matriz = team.length >= 1 && team.length <= 6;
  const LARGO = '@container servicos (min-width: 560px)';
  return (
    <Section
      id="pl-sec-servicos"
      icon={DesignServicesRounded}
      title="Quem faz o quê aqui"
      subtitle={cat.label}
      container="servicos"
      action={<Button onClick={() => a.onManageCatalog?.(detail.id)} startIcon={<TuneRounded sx={{ fontSize: 18 }} />} sx={OUTLINE_BTN_SX}>Serviços</Button>}
    >
      {cat.withoutProfessionalLabel && <ContextLine icon={WarningAmberRounded} tone="warning">{cat.withoutProfessionalLabel}</ContextLine>}
      {groups.length === 0 ? (
        <EmptyNote icon={DesignServicesRounded} tone="error">{cat.label}</EmptyNote>
      ) : (
        <>
          {matriz && (
            <Box sx={{ display: 'none', [LARGO]: { display: 'block' } }}>
              <WhoDoesWhatTable groups={groups} team={team} placeId={detail.id} a={a} />
            </Box>
          )}
          <Box sx={matriz ? { [LARGO]: { display: 'none' } } : undefined}>
            <ServiceList groups={groups} placeId={detail.id} a={a} />
          </Box>
        </>
      )}
    </Section>
  );
}

/* ── Preços por forma de pagamento ──────────────────────────────────────── */
function PriceValue({ price, custom }) {
  const label = price.priceLabel;
  const valor = !price.isToBeAgreed;
  const igual = !!price.isDefault;
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, justifyContent: 'flex-end' }}>
      <Typography
        variant="caption"
        component="span"
        sx={{
          ...NUM, whiteSpace: valor ? 'nowrap' : 'normal',
          color: igual || !valor ? 'var(--text-secondary)' : 'var(--text-primary)',
          fontWeight: igual || !valor ? 'var(--fw-regular)' : 'var(--fw-bold)',
          fontStyle: valor ? 'normal' : 'italic',
        }}
      >
        {label}
      </Typography>
      {custom && (
        <>
          <StarRounded sx={{ fontSize: 14, color: 'var(--color-primary-dark)', flexShrink: 0 }} aria-hidden="true" />
          <Box component="span" sx={SR_ONLY}>{`, ${custom}`}</Box>
        </>
      )}
    </Box>
  );
}

function PriceTable({ groups, pays, placeId, a }) {
  return (
    <Box component="table" sx={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, tableLayout: 'fixed' }}>
      <Box component="caption" sx={SR_ONLY}>Preço de cada serviço em cada forma de pagamento</Box>
      <Box component="colgroup">
        <Box component="col" sx={{ width: pays.length > 5 ? '24%' : '30%' }} />
        <Box component="col" />
        {pays.map((p) => <Box component="col" key={p.id} />)}
      </Box>
      <Box component="thead">
        <Box component="tr">
          <Box component="th" scope="col" sx={TH_SX}><Typography variant="overline" sx={OVERLINE}>Serviço</Typography></Box>
          <Box component="th" scope="col" sx={{ ...TH_SX, textAlign: 'right', px: 0.5 }}>
            <Typography variant="caption" sx={{ ...HEAD, color: 'var(--text-secondary)', display: 'block' }}>Preço padrão</Typography>
          </Box>
          {pays.map((p) => {
            const Icon = payIcon(p.paymentType);
            return (
              <Box component="th" scope="col" key={p.id} sx={{ ...TH_SX, textAlign: 'right', px: 0.5 }}>
                <Icon sx={{ fontSize: 16, color: 'var(--text-secondary)', display: 'block', ml: 'auto', mb: 0.25 }} aria-hidden="true" />
                <Typography variant="caption" sx={{ ...HEAD, color: 'var(--text-primary)', display: 'block', lineHeight: 1.25, ...BREAK }}>{p.name}</Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
      <Box component="tbody">
        {groups.map((g) => (
          <Fragment key={g.category}>
            <Box component="tr">
              <Box component="th" scope="colgroup" colSpan={pays.length + 2} sx={{ ...TH_SX, pt: 1.75, pb: 0.5 }}>
                <Typography variant="caption" sx={{ ...HEAD, color: 'var(--text-secondary)' }}>{g.category}</Typography>
              </Box>
            </Box>
            {(g.items || []).map((it) => {
              const porForma = Object.fromEntries((it.prices || []).map((p) => [p.paymentName, p]));
              return (
                <Box component="tr" key={it.serviceId} sx={{ '&:hover td, &:hover th': { bgcolor: 'var(--bg-page)' } }}>
                  <Box component="th" scope="row" sx={{ ...TD_SX, textAlign: 'left', fontWeight: 'inherit', pl: 0 }}>
                    <ButtonBase
                      onClick={() => a.onConfigureServicePrices?.(placeId, it.serviceId)}
                      aria-label={`Preços de ${it.name}`}
                      sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 0.5, minHeight: 40, width: '100%',
                        textAlign: 'left', borderRadius: 'var(--radius-sm)', px: 0.5, ml: -0.5,
                        '& .pl-edit': { opacity: 0, transition: 'opacity .15s' }, '&:hover .pl-edit, &:focus-visible .pl-edit': { opacity: 1 }, ...FOCUS,
                      }}
                    >
                      <Typography variant="body2" component="span" sx={{ ...HEAD, ...BREAK, color: 'var(--text-primary)' }}>{it.name}</Typography>
                      <EditRounded className="pl-edit" sx={{ fontSize: 16, color: 'var(--color-primary-dark)', flexShrink: 0 }} aria-hidden="true" />
                    </ButtonBase>
                  </Box>
                  <Box component="td" sx={{ ...TD_SX, textAlign: 'right' }}>
                    <Typography variant="caption" sx={{ ...NUM, ...HEAD, color: 'var(--text-primary)', fontStyle: it.basePriceIsToBeAgreed ? 'italic' : 'normal' }}>{it.basePriceLabel}</Typography>
                  </Box>
                  {pays.map((p) => {
                    const pr = porForma[p.name];
                    return (
                      <Box component="td" key={p.id} sx={{ ...TD_SX, textAlign: 'right' }}>
                        {pr ? <PriceValue price={pr} custom={pr.customLabel} /> : (
                          <>
                            <Typography variant="caption" aria-hidden="true" sx={{ color: 'var(--text-disabled)' }}>—</Typography>
                            <Box component="span" sx={SR_ONLY}>sem preço nesta forma</Box>
                          </>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              );
            })}
          </Fragment>
        ))}
      </Box>
    </Box>
  );
}

function PriceCards({ groups, placeId, a }) {
  return (
    <Box component="ul" sx={{ ...LIST_RESET, display: 'grid', gap: 1, alignItems: 'start', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 300px), 1fr))' }}>
      {groups.flatMap((g) => (g.items || []).map((it) => (
        <Box component="li" key={it.serviceId} sx={{ p: 1.25, borderRadius: 'var(--radius-sm)', border: '1px solid', borderColor: 'var(--divider)', display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>{g.category}</Typography>
              <Typography variant="body2" component="p" sx={{ ...HEAD, ...BREAK, color: 'var(--text-primary)' }}>{it.name}</Typography>
            </Box>
            <Tooltip title="Editar preços">
              <IconButton aria-label={`Preços de ${it.name}`} onClick={() => a.onConfigureServicePrices?.(placeId, it.serviceId)} sx={{ ...ICON_BTN_SX, mt: -0.5, mr: -0.5 }}>
                <EditRounded sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Box>
          <Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', columnGap: 1.5, rowGap: 0.5, alignItems: 'center' }}>
            <Box component="dt" sx={{ minWidth: 0 }}>
              <Typography variant="caption" sx={{ ...HEAD, color: 'var(--text-secondary)' }}>Preço padrão</Typography>
            </Box>
            <Box component="dd" sx={{ m: 0, textAlign: 'right' }}>
              <Typography variant="caption" sx={{ ...NUM, ...HEAD, color: 'var(--text-primary)', fontStyle: it.basePriceIsToBeAgreed ? 'italic' : 'normal' }}>{it.basePriceLabel}</Typography>
            </Box>
            {(it.prices || []).map((p) => {
              const Icon = payIcon(p.paymentType);
              return (
                <Fragment key={p.paymentName}>
                  <Box component="dt" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, color: 'var(--text-secondary)' }}>
                    <Icon sx={{ fontSize: 14, flexShrink: 0 }} aria-hidden="true" />
                    <Typography variant="caption" sx={{ color: 'var(--text-primary)', ...BREAK }}>{p.paymentName}</Typography>
                  </Box>
                  <Box component="dd" sx={{ m: 0, textAlign: 'right' }}>
                    <PriceValue price={p} custom={p.customLabel} />
                  </Box>
                  {p.customLabel && (
                    <Box component="dd" sx={{ m: 0, gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 0.5, color: 'var(--color-primary-dark)', mt: -0.25 }}>
                      <StarRounded sx={{ fontSize: 14 }} aria-hidden="true" />
                      <Typography variant="caption" sx={{ color: 'var(--text-primary)' }}>{p.customLabel}</Typography>
                    </Box>
                  )}
                </Fragment>
              );
            })}
          </Box>
        </Box>
      )))}
    </Box>
  );
}

function AcceptedPayments({ pays }) {
  return (
    <Box component="ul" aria-label="Formas aceitas" sx={{ ...LIST_RESET, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
      {pays.map((p) => {
        const Icon = payIcon(p.paymentType);
        const chip = (
          <Box
            tabIndex={p.removalImpactLabel ? 0 : undefined}
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 1, py: 0.5, borderRadius: 'var(--radius-sm)', border: '1px solid', borderColor: 'var(--divider)', bgcolor: 'var(--surface)', minHeight: 40, ...FOCUS }}
          >
            <Icon sx={{ fontSize: 18, color: 'var(--color-primary-dark)' }} aria-hidden="true" />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" component="span" sx={{ ...HEAD, color: 'var(--text-primary)', display: 'block', lineHeight: 1.2 }}>{p.name}</Typography>
              <Typography variant="caption" component="span" sx={{ color: 'var(--text-secondary)', display: 'block', lineHeight: 1.2 }}>{p.usageLabel}</Typography>
            </Box>
            {p.removalImpactLabel && <Box component="span" sx={SR_ONLY}>{`. ${p.removalImpactLabel}`}</Box>}
          </Box>
        );
        return (
          <Box component="li" key={p.id}>
            {p.removalImpactLabel ? <Tooltip title={p.removalImpactLabel}>{chip}</Tooltip> : chip}
          </Box>
        );
      })}
    </Box>
  );
}

function PricesSection({ detail, a }) {
  const pays = detail.payments || [];
  const groups = detail.catalog || [];
  const card = detail.card || {};
  const items = groups.flatMap((g) => g.items || []);
  const proprios = items.flatMap((it) => (it.prices || []).filter((p) => p.customLabel).map((p) => ({ key: `${it.serviceId}-${p.paymentName}`, servico: it.name, forma: p.paymentName, frase: p.customLabel })));
  // A matriz só aparece quando cabe sem espremer (nunca com rolagem lateral); antes disso, uma lista por serviço.
  const minimo = pays.length <= 4 ? 480 : pays.length <= 6 ? 600 : pays.length <= 8 ? 680 : 900;
  const LARGO = `@container precos (min-width: ${minimo}px)`;
  return (
    <Section
      id="pl-sec-precos"
      icon={PaymentsRounded}
      title="Preços por forma de pagamento"
      subtitle={card.payments?.isConfigured ? card.payments.label : null}
      container="precos"
      action={<Button onClick={() => a.onManagePayments?.(detail.id)} startIcon={<TuneRounded sx={{ fontSize: 18 }} />} sx={OUTLINE_BTN_SX}>Formas de pagamento</Button>}
    >
      {pays.length === 0 ? (
        <>
          <EmptyNote icon={InfoOutlined} tone="info">{card.payments?.emptyLabel}</EmptyNote>
          {items.length > 0 && (
            <Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', columnGap: 0, rowGap: 0 }}>
              {items.map((it) => (
                <Fragment key={it.serviceId}>
                  <Box component="dt" sx={{ minWidth: 0, py: 0.75, borderBottom: '1px solid', borderColor: 'var(--divider)' }}>
                    <Typography variant="body2" sx={{ color: 'var(--text-primary)', ...BREAK }}>{it.name}</Typography>
                  </Box>
                  <Box component="dd" sx={{ m: 0, py: 0.75, pl: 2, textAlign: 'right', borderBottom: '1px solid', borderColor: 'var(--divider)' }}>
                    <Typography variant="body2" sx={{ ...HEAD, ...NUM, color: 'var(--text-primary)', fontStyle: it.basePriceIsToBeAgreed ? 'italic' : 'normal' }}>{it.basePriceLabel}</Typography>
                  </Box>
                </Fragment>
              ))}
            </Box>
          )}
        </>
      ) : (
        <>
          <AcceptedPayments pays={pays} />
          {items.length > 0 && (
            <>
              <Box sx={{ display: 'none', [LARGO]: { display: 'block' } }}>
                <PriceTable groups={groups} pays={pays} placeId={detail.id} a={a} />
              </Box>
              <Box sx={{ [LARGO]: { display: 'none' } }}>
                <PriceCards groups={groups} placeId={detail.id} a={a} />
              </Box>
            </>
          )}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 2, rowGap: 0.5, color: 'var(--text-secondary)' }}>
            <Typography variant="caption" sx={{ color: 'inherit' }}>Em cinza: igual ao preço padrão.</Typography>
            <Typography variant="caption" sx={{ color: 'inherit' }}>Em negrito: diferente do padrão.</Typography>
            <Typography variant="caption" sx={{ color: 'inherit', fontStyle: 'italic' }}>Em itálico: a combinar.</Typography>
          </Box>
          {proprios.length > 0 && (
            <Box component="ul" aria-label="Preços próprios de profissionais" sx={{ ...LIST_RESET, display: 'flex', flexDirection: 'column', gap: 0.5, p: 1.25, borderRadius: 'var(--radius-sm)', bgcolor: 'var(--color-primary-soft)' }}>
              {proprios.map((x) => (
                <Box component="li" key={x.key} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
                  <StarRounded sx={{ fontSize: 16, color: 'var(--color-primary-dark)', mt: '1px', flexShrink: 0 }} aria-hidden="true" />
                  <Typography variant="caption" sx={{ color: 'var(--text-primary)', ...BREAK }}>
                    <Box component="span" sx={HEAD}>{`${x.servico} · ${x.forma}: `}</Box>{x.frase}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </>
      )}
    </Section>
  );
}

/* ── O que o lugar vive: agendas, movimento, páginas, ausências ──────────── */
function SchedulesSection({ detail, a }) {
  const list = detail.schedules || [];
  return (
    <Section id="pl-sec-agendas" icon={CalendarMonthRounded} title="Agendas daqui">
      {list.length === 0 ? (
        <EmptyNote icon={CalendarMonthRounded}>Nenhuma agenda aqui ainda.</EmptyNote>
      ) : (
        <Box component="ul" sx={{ ...LIST_RESET, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          {list.map((s) => {
            const mode = s.isByChoice ? 'waiting' : s.status === 'success' ? 'live' : 'alert';
            const tone = mode === 'live' ? 'success' : mode === 'waiting' ? 'neutral' : (s.status === 'warning' ? 'warning' : 'error');
            const t = tint(tone);
            const Icon = mode === 'live' ? CheckCircleRounded : mode === 'waiting' ? PauseCircleRounded : EventBusyRounded;
            return (
              <Box component="li" key={s.id}>
                <ButtonBase
                  onClick={() => a.onOpenSchedule?.(s.id)}
                  aria-label={`Abrir a agenda ${s.name}, de ${s.professionalName}, em Agendas`}
                  sx={{
                    width: '100%', display: 'flex', alignItems: 'stretch', gap: 1, p: 1.25, textAlign: 'left', justifyContent: 'flex-start',
                    borderRadius: 'var(--radius-sm)', border: '1px solid', borderColor: mode === 'alert' ? t.border : 'var(--divider)',
                    transition: 'border-color .15s, background-color .15s',
                    '&:hover': { borderColor: 'var(--color-primary-light)', bgcolor: 'var(--bg-page)' }, ...FOCUS,
                  }}
                >
                  <Box sx={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" component="span" sx={{ ...HEAD, color: 'var(--text-primary)', display: 'block', ...BREAK }}>{s.name}</Typography>
                      <Typography variant="caption" sx={{ color: 'var(--text-secondary)', ...BREAK }}>{s.professionalName}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: t.fg }}>
                      <Icon sx={{ fontSize: 16, flexShrink: 0 }} aria-hidden="true" />
                      <Typography variant="caption" sx={{ ...HEAD, color: 'inherit' }}>{s.headline}</Typography>
                    </Box>
                    <WeekStrip days={s.daysActive} mode={mode} tone={tone} />
                    {s.weekLabel && <Typography variant="caption" sx={{ color: 'var(--text-secondary)', ...NUM }}>{s.weekLabel}</Typography>}
                    {s.capacityLabel && <ContextLine icon={GroupsRounded}>{s.capacityLabel}</ContextLine>}
                    {s.activityLabel && <ContextLine icon={EventAvailableRounded}>{s.activityLabel}</ContextLine>}
                  </Box>
                  <ChevronRightRounded sx={{ color: 'var(--text-secondary)', alignSelf: 'center', flexShrink: 0 }} aria-hidden="true" />
                </ButtonBase>
              </Box>
            );
          })}
        </Box>
      )}
    </Section>
  );
}

function ActivitySection({ detail, a }) {
  const stats = detail.activityStats || [];
  const act = detail.card?.activity || {};
  // "Aguardando confirmação" com algo esperando vira um BOTÃO (leva a Agendamentos), na ordem do vm.
  const esperaAtiva = (s) => s.key === 'awaitingConfirmation' && act.awaitingConfirmationCount > 0;
  const numeros = stats.filter((s) => s.key !== 'busiestProfessional' && !esperaAtiva(s));
  const espera = stats.filter(esperaAtiva);
  const frases = stats.filter((s) => s.key === 'busiestProfessional');
  return (
    <Section id="pl-sec-movimento" icon={InsightsRounded} title="Movimento">
      {stats.length === 0 ? (
        <EmptyNote icon={AutoAwesomeRounded}>{detail.activityEmptyLabel || act.emptyLabel || act.lastAppointmentLabel}</EmptyNote>
      ) : (
        <>
          {act.nextAppointmentLabel && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.25, borderRadius: 'var(--radius-sm)', bgcolor: 'var(--color-primary-soft)', color: 'var(--color-primary-dark)' }}>
              <Box
                aria-hidden="true"
                sx={{
                  width: 10, height: 10, borderRadius: '50%', bgcolor: 'var(--color-primary)', flexShrink: 0,
                  '@keyframes plPulso': { '0%': { boxShadow: '0 0 0 0 var(--color-primary-light)' }, '70%, 100%': { boxShadow: '0 0 0 8px transparent' } },
                  animation: 'plPulso 2.4s ease-out infinite', [REDUCED]: { animation: 'none' },
                }}
              />
              <Typography variant="body2" sx={{ ...HEAD, color: 'var(--text-primary)' }}>{act.nextAppointmentLabel}</Typography>
            </Box>
          )}
          {numeros.length > 0 && (
            <Box component="dl" sx={{ m: 0, display: 'grid', gap: 1, gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 120px), 1fr))' }}>
              {numeros.map((s) => (
                <Box key={s.key} sx={{ p: 1.25, borderRadius: 'var(--radius-sm)', bgcolor: 'var(--bg-page)', display: 'flex', flexDirection: 'column-reverse', justifyContent: 'flex-end', gap: 0.25, minWidth: 0 }}>
                  <Box component="dt" sx={{ minWidth: 0 }}>
                    <Typography variant="caption" sx={{ color: 'var(--text-secondary)', textWrap: 'pretty' }}>{s.label}</Typography>
                    {s.hint && <Typography variant="caption" sx={{ color: 'var(--text-secondary)', fontStyle: 'italic', textWrap: 'pretty' }}>{s.hint}</Typography>}
                  </Box>
                  <Box component="dd" sx={{ m: 0 }}>
                    <Typography variant="h5" component="span" sx={{ ...HEAD, ...NUM, color: 'var(--text-primary)', lineHeight: 1.1 }}>{s.value}</Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
          {espera.map((s) => (
            <ButtonBase
              key={s.key}
              onClick={() => a.onOpenAwaitingConfirmation?.(detail.id)}
              sx={{
                width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 1.25, p: 1.25, textAlign: 'left',
                borderRadius: 'var(--radius-sm)', bgcolor: 'var(--color-primary-soft)', color: 'var(--color-primary-dark)',
                border: '1px solid', borderColor: 'color-mix(in srgb, var(--color-primary) 30%, transparent)',
                transition: 'border-color .15s', '&:hover': { borderColor: 'var(--color-primary)' },
                '&:hover .pl-seta': { transform: 'translateX(3px)' }, ...FOCUS,
              }}
            >
              <HourglassTopRounded sx={{ fontSize: 22, flexShrink: 0 }} aria-hidden="true" />
              <Typography variant="h5" component="span" sx={{ ...HEAD, ...NUM, color: 'var(--text-primary)', lineHeight: 1.1 }}>{s.value}</Typography>
              <Box component="span" sx={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography variant="body2" component="span" sx={{ ...HEAD, color: 'var(--text-primary)', textWrap: 'pretty' }}>{s.label}</Typography>
                {s.hint && <Typography variant="caption" component="span" sx={{ color: 'var(--color-primary-dark)', textWrap: 'pretty' }}>{s.hint}</Typography>}
              </Box>
              <ArrowForwardRounded className="pl-seta" sx={{ fontSize: 18, flexShrink: 0, transition: 'transform .2s', [REDUCED]: { transition: 'none' } }} aria-hidden="true" />
            </ButtonBase>
          ))}
          {frases.map((s) => (
            <Box key={s.key} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <StarRounded sx={{ fontSize: 18, color: 'var(--color-primary-dark)', mt: '2px', flexShrink: 0 }} aria-hidden="true" />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>{s.label}</Typography>
                <Typography variant="body2" sx={{ ...HEAD, color: 'var(--text-primary)', ...BREAK }}>{s.value}</Typography>
              </Box>
            </Box>
          ))}
        </>
      )}
    </Section>
  );
}

function PagesSection({ detail, a }) {
  const pages = detail.pages || [];
  const pres = detail.card?.presence || {};
  return (
    <Section id="pl-sec-paginas" icon={PublicRounded} title="Onde aparece" subtitle={pres.label}>
      {pages.length === 0 ? (
        <EmptyNote icon={PublicRounded}>Ainda não aparece em nenhuma página no ar.</EmptyNote>
      ) : (
        <Box component="ul" sx={{ ...LIST_RESET, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          {pages.map((p) => (
            <Box component="li" key={p.id}>
              <ButtonBase
                onClick={() => a.onOpenPage?.(p.url)}
                aria-label={`Abrir a página ${p.title}`}
                sx={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 1, p: 1.25, textAlign: 'left', justifyContent: 'flex-start',
                  borderRadius: 'var(--radius-sm)', border: '1px solid', borderColor: 'var(--divider)',
                  '&:hover': { borderColor: 'var(--color-primary-light)', bgcolor: 'var(--bg-page)' }, ...FOCUS,
                }}
              >
                <LanguageRounded sx={{ fontSize: 20, color: 'var(--color-primary-dark)', flexShrink: 0 }} aria-hidden="true" />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="body2" component="span" sx={{ ...HEAD, color: 'var(--text-primary)', display: 'block', ...BREAK }}>{p.title}</Typography>
                  <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>{[p.typeLabel, p.scopeLabel].filter(Boolean).join(' · ')}</Typography>
                </Box>
                <OpenInNewRounded sx={{ fontSize: 18, color: 'var(--text-secondary)', flexShrink: 0 }} aria-hidden="true" />
              </ButtonBase>
            </Box>
          ))}
        </Box>
      )}
    </Section>
  );
}

function AbsencesSection({ detail }) {
  const list = detail.absences || [];
  if (!list.length) return null;
  return (
    <Section id="pl-sec-ausencias" icon={BeachAccessRounded} title="Ausências de quem atende aqui">
      <Box component="ul" sx={{ ...LIST_RESET, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        {list.map((x) => {
          const t = tint(x.isNow ? 'warning' : 'info');
          return (
            <Box component="li" key={x.id} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, p: 1.25, borderRadius: 'var(--radius-sm)', bgcolor: t.bg }}>
              <BeachAccessRounded sx={{ fontSize: 18, color: t.fg, mt: '1px', flexShrink: 0 }} aria-hidden="true" />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                  <Typography variant="body2" sx={{ ...HEAD, color: 'var(--text-primary)', ...BREAK }}>{x.professionalName}</Typography>
                  {x.isNow && (
                    <Box sx={{ px: 0.75, borderRadius: 999, border: '1px solid', borderColor: t.border, bgcolor: 'var(--surface)' }}>
                      <Typography variant="caption" sx={{ ...HEAD, color: t.fg }}>Agora</Typography>
                    </Box>
                  )}
                </Box>
                <Typography variant="caption" sx={{ color: t.fg, ...NUM }}>{[x.label, x.reason].filter(Boolean).join(' · ')}</Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Section>
  );
}

function PlaceDetail({ vm, detail, a, headRef, rootRef, single }) {
  const labels = vm.labels || {};
  return (
    <Box ref={rootRef} component="section" aria-labelledby="pl-aberto-titulo" sx={{ ...rise(0), ...SCROLL_MARGIN, containerType: 'inline-size', containerName: 'aberto' }}>
      {!single && <DetailNav items={vm.items || []} selectedId={detail.id} labels={labels} a={a} />}
      <DetailHero detail={detail} a={a} headRef={headRef} />
      <Box
        sx={{
          display: 'grid', gap: 'var(--space-2)', gridTemplateColumns: 'minmax(0, 1fr)', alignItems: 'start',
          '@container aberto (min-width: 980px)': { gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 340px)' },
        }}
      >
        <Box
          sx={{
            display: 'grid', gap: 'var(--space-2)', gridTemplateColumns: 'minmax(0, 1fr)', alignItems: 'start', minWidth: 0,
            '@container aberto (min-width: 1560px)': { gridTemplateColumns: 'minmax(0, 0.85fr) minmax(0, 1.15fr)', '& > .pl-largo': { gridColumn: '1 / -1' } },
          }}
        >
          <TeamSection detail={detail} a={a} />
          <ServicesSection detail={detail} a={a} />
          <Box className="pl-largo" sx={{ minWidth: 0 }}>
            <PricesSection detail={detail} a={a} />
          </Box>
        </Box>
        {/* Ao lado (≥ 980 px) é uma coluna; abaixo disso as seções se repartem em até 3 colunas. */}
        <Box sx={{ display: 'grid', gap: 'var(--space-2)', alignItems: 'start', minWidth: 0, gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 300px), 1fr))' }}>
          <SchedulesSection detail={detail} a={a} />
          <ActivitySection detail={detail} a={a} />
          <PagesSection detail={detail} a={a} />
          <AbsencesSection detail={detail} />
        </Box>
      </Box>
    </Box>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Topo, estados
   ═══════════════════════════════════════════════════════════════════════════ */

/** O botão de cadastrar (o texto vem em labels.actionLabel) — com o plano cheio, a explicação vem ANTES do clique, colada no botão. */
function NewPlace({ labels, plan, a }) {
  const bloqueado = !!plan && !plan.canCreate;
  const t = tint(plan?.status);
  const pct = plan && plan.limit ? Math.min(100, Math.round((plan.used / plan.limit) * 100)) : 0;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'stretch', sm: 'flex-end' }, gap: 0.75, width: { xs: '100%', sm: 'auto' }, maxWidth: { sm: 380 } }}>
      <Button
        onClick={() => a.onNew?.()}
        disableElevation
        startIcon={bloqueado ? <WorkspacePremiumRounded /> : <AddRounded />}
        aria-describedby={plan ? 'pl-plano' : undefined}
        sx={bloqueado
          ? { ...OUTLINE_BTN_SX, minHeight: 42, px: 2.25, borderColor: 'var(--color-primary)', color: 'var(--color-primary-dark)', fontWeight: 'var(--fw-bold)' }
          : PRIMARY_BTN_SX}
      >
        {labels.actionLabel}
      </Button>
      {plan && (
        <Box id="pl-plano" sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: { sm: 'flex-end' }, textAlign: { sm: 'right' } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box aria-hidden="true" sx={{ width: 72, height: 6, borderRadius: 3, bgcolor: 'var(--divider)', overflow: 'hidden', flexShrink: 0 }}>
              <Box sx={{ width: `${pct}%`, height: '100%', bgcolor: t.main }} />
            </Box>
            <Typography variant="caption" sx={{ ...HEAD, color: t.fg }}>{plan.label}</Typography>
          </Box>
          {bloqueado && plan.message && (
            <Typography variant="caption" sx={{ color: 'var(--text-secondary)', textWrap: 'pretty' }}>{plan.message}</Typography>
          )}
        </Box>
      )}
    </Box>
  );
}

function StateBlock({ icon, title, description, action }) {
  return (
    <Box sx={{ ...CARD_SX, px: 3, py: { xs: 5, sm: 7 }, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
      <Box sx={{ width: 64, height: 64, borderRadius: '50%', mb: 2, bgcolor: 'var(--bg-page)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{icon}</Box>
      <Typography variant="subtitle1" component="h2" sx={{ ...HEAD, color: 'var(--text-primary)', mb: 0.5 }}>{title}</Typography>
      {description && <Typography variant="body2" sx={{ color: 'var(--text-secondary)', maxWidth: 440 }}>{description}</Typography>}
      {action && <Box sx={{ mt: 2.5 }}>{action}</Box>}
    </Box>
  );
}

/** Ilustração do vazio: os exemplos do nicho virando lugares que recebem agendamento. */
function PlacesSketch({ examples }) {
  const nomes = (examples || []).slice(0, 3);
  const lista = nomes.length ? nomes : ['', '', ''];
  return (
    <Box aria-hidden="true" sx={{ width: '100%', maxWidth: 340, mx: 'auto', display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      {lista.map((nome, i) => (
        <Box
          key={`${nome}-${i}`}
          sx={{
            bgcolor: 'var(--surface)', border: '1px solid', borderColor: 'var(--divider)', borderRadius: 'var(--radius-card)',
            boxShadow: i === 0 ? 'var(--shadow-hover)' : 'var(--shadow-card)', p: 1.5, display: 'flex', alignItems: 'center', gap: 1.25,
            ml: i * 2, mr: (2 - i) * 2,
            // Chega uma vez e fica (nada de laço infinito disputando a atenção com o botão).
            '@keyframes plChega': { from: { opacity: 0, transform: 'translateY(8px)' }, to: { opacity: 1, transform: 'none' } },
            animation: 'plChega .6s cubic-bezier(.2,.7,.3,1) both', animationDelay: `${150 + i * 160}ms`,
            [REDUCED]: { animation: 'none' },
          }}
        >
          <Box sx={{ width: 36, height: 36, borderRadius: 'var(--radius-sm)', bgcolor: 'var(--color-primary-soft)', color: 'var(--color-primary-dark)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <StorefrontRounded sx={{ fontSize: 20 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {nome ? (
              <Typography variant="caption" sx={{ ...HEAD, color: 'var(--text-primary)', ...ONE_LINE }}>{nome}</Typography>
            ) : (
              <Box sx={{ height: 8, width: '58%', borderRadius: 4, bgcolor: 'var(--divider)' }} />
            )}
            <Box sx={{ display: 'flex', gap: '3px' }}>
              {DAY_INITIAL.map((_, d) => (
                <Box key={d} sx={{ flex: 1, height: 6, borderRadius: 2, bgcolor: d < 5 ? 'var(--color-primary-soft)' : 'var(--bg-page)' }} />
              ))}
            </Box>
          </Box>
          <CheckCircleRounded
            sx={{
              fontSize: 20, color: 'var(--color-success)', flexShrink: 0,
              '@keyframes plOk': { from: { opacity: 0, transform: 'scale(.6)' }, to: { opacity: 1, transform: 'none' } },
              animation: 'plOk .35s ease-out both', animationDelay: `${700 + i * 220}ms`,
              [REDUCED]: { animation: 'none' },
            }}
          />
        </Box>
      ))}
    </Box>
  );
}

function EmptyHero({ empty, a }) {
  const ex = empty.examples || [];
  return (
    <Box sx={{ ...CARD_SX, ...rise(0), containerType: 'inline-size', overflow: 'hidden' }}>
      <Box
        sx={{
          p: { xs: 2.5, sm: 4 }, display: 'grid', gap: 4, alignItems: 'center', gridTemplateColumns: 'minmax(0, 1fr)',
          '@container (min-width: 760px)': { gridTemplateColumns: 'minmax(0, 1.1fr) minmax(0, 1fr)', gap: 6, p: 5 },
        }}
      >
        <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1.5, maxWidth: 560 }}>
          <Typography variant="h5" component="h2" sx={{ ...HEAD, color: 'var(--text-primary)', textWrap: 'pretty' }}>{empty.title}</Typography>
          <Typography variant="body1" sx={{ color: 'var(--text-secondary)', textWrap: 'pretty' }}>{empty.body}</Typography>
          {ex.length > 0 && (
            <Box sx={{ mt: 0.5 }}>
              <Typography variant="caption" component="p" sx={{ color: 'var(--text-secondary)', mb: 0.75 }}>Por exemplo:</Typography>
              <Box component="ul" sx={{ ...LIST_RESET, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {ex.map((e) => (
                  <Box component="li" key={e} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 1.25, py: 0.75, borderRadius: 999, bgcolor: 'var(--bg-page)', border: '1px solid', borderColor: 'var(--divider)' }}>
                    <StorefrontRounded sx={{ fontSize: 16, color: 'var(--color-primary-dark)' }} aria-hidden="true" />
                    <Typography variant="caption" sx={{ color: 'var(--text-primary)', fontWeight: 'var(--fw-medium)' }}>{e}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
          {empty.actionLabel && (
            <Box sx={{ mt: 1.5 }}>
              <Button onClick={() => a.onNew?.()} disableElevation startIcon={<AddRounded />} sx={CTA_BTN_SX}>{empty.actionLabel}</Button>
            </Box>
          )}
        </Box>
        <PlacesSketch examples={ex} />
      </Box>
    </Box>
  );
}

const SK = { bgcolor: 'var(--bg-page)' };

/** Skeleton com a FORMA do que vem: faixa de resumo + régua, filtros e cartões (monograma, rostos, linhas). */
function LoadingSkeleton() {
  return (
    <Box role="status" aria-busy="true">
      <Box component="span" sx={SR_ONLY}>Carregando…</Box>
      <Box aria-hidden="true">
        <Box sx={{ ...CARD_SX, p: 2.25, mb: 'var(--space-3)', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
            <Skeleton variant="rounded" width={28} height={28} sx={{ ...SK, borderRadius: '6px' }} />
            <Skeleton variant="text" width={110} height={30} sx={SK} />
            {[170, 190].map((w, i) => <Skeleton key={i} variant="rounded" width={w} height={44} sx={{ ...SK, borderRadius: 'var(--radius-sm)' }} />)}
            <Box sx={{ flex: '1 1 160px', display: 'flex', gap: '3px' }}>
              {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} variant="rounded" height={8} sx={{ ...SK, flex: 1, maxWidth: 72, borderRadius: 3 }} />)}
            </Box>
          </Box>
          <Skeleton variant="text" width="min(520px, 80%)" sx={SK} />
        </Box>
        <Box sx={{ ...CARD_SX, p: 1.75, mb: 'var(--space-3)', display: 'flex', flexWrap: 'wrap', gap: 1.25 }}>
          <Skeleton variant="rounded" height={42} sx={{ ...SK, flex: '2 1 260px', borderRadius: 'var(--radius-sm)' }} />
          <Skeleton variant="rounded" height={42} sx={{ ...SK, flex: '1 1 190px', borderRadius: 'var(--radius-sm)' }} />
          <Skeleton variant="rounded" width={92} height={42} sx={{ ...SK, borderRadius: 'var(--radius-sm)' }} />
        </Box>
        <Box sx={CARDS_GRID}>
          {Array.from({ length: 3 }).map((_, i) => (
            <Box key={i} sx={{ ...CARD_SX, p: 2.25, display: 'flex', flexDirection: 'column', gap: 1.5, '& .MuiSkeleton-root': { animationDelay: `${i * 140}ms` } }}>
              <Skeleton variant="text" width="44%" sx={SK} />
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                <Skeleton variant="rounded" width={48} height={48} sx={{ ...SK, borderRadius: 'var(--radius-sm)' }} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton variant="text" width={`${58 + (i % 2) * 14}%`} height={26} sx={SK} />
                  <Skeleton variant="text" width="48%" sx={SK} />
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pt: 1.25, borderTop: '1px solid', borderColor: 'var(--divider)' }}>
                <Box sx={{ display: 'flex' }}>
                  {[0, 1, 2].map((d) => <Skeleton key={d} variant="circular" width={28} height={28} sx={{ ...SK, ml: d ? -0.75 : 0, border: '2px solid var(--surface)' }} />)}
                </Box>
                <Skeleton variant="text" width="36%" sx={SK} />
              </Box>
              <Skeleton variant="text" width="62%" sx={SK} />
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   View
   ═══════════════════════════════════════════════════════════════════════════ */

export default function PlacesView({ vm, actions }) {
  const a = actions || {};
  const labels = vm.labels || {};
  const items = vm.items || [];
  const f = vm.filters || {};
  const st = vm.viewState;
  const detail = vm.detail && vm.selectedId ? vm.detail : null;
  const single = st === 'ready' && items.length === 1 && !f.isFiltered;
  const firstAlert = items.find((it) => pesoDe(it.readiness) === 'alert');

  const [flash, setFlash] = useState(null);   // UI: cartão realçado depois de "ir até ele"
  const timers = useRef([]);
  const pageTitleRef = useRef(null);
  const detailHeadRef = useRef(null);
  const detailRootRef = useRef(null);
  const openRefs = useRef({});   // id → botão que abre o lugar (para onde o foco volta)
  const cardRefs = useRef({});   // id → o cartão (destino do "ir até ele")

  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  // Abriu (ou trocou de) lugar → foco no título dele, sem pular a página. Fechou → o foco volta para
  // quem abriu. No primeiro desenho (o mock já pode nascer aberto) nada se mexe.
  const abertoAntes = useRef(detail ? detail.id : null);
  useEffect(() => {
    const antes = abertoAntes.current;
    const agora = detail ? detail.id : null;
    abertoAntes.current = agora;
    if (antes === agora) return;
    if (agora) {
      levarFoco(detailHeadRef.current, false);
      mostrarInicio(detailRootRef.current);
      return;
    }
    if (focoPerdido()) levarFoco(openRefs.current[antes] || cardRefs.current[antes] || pageTitleRef.current);
  }, [detail]);

  const jump = (id) => {
    const el = cardRefs.current[id];
    if (!el) return;
    levarFoco(el);
    setFlash(id);
    timers.current.push(setTimeout(() => setFlash((x) => (x === id ? null : x)), 1800));
  };

  const regOpen = (id) => (el) => { openRefs.current[id] = el; };
  const regCard = (id) => (el) => { cardRefs.current[id] = el; };

  let body = null;
  let showSummary = false;
  let showFilters = false;
  if (st === 'loading') {
    body = <LoadingSkeleton />;
  } else if (st === 'error') {
    body = (
      <StateBlock
        icon={<ErrorRounded sx={{ fontSize: 32, color: 'var(--color-error)' }} />}
        title={vm.errorMessage || 'Não foi possível carregar agora.'}
        action={<Button onClick={() => a.onRetry?.()} startIcon={<ReplayRounded />} sx={{ ...OUTLINE_BTN_SX, minHeight: 42, borderColor: 'var(--color-primary)', color: 'var(--color-primary-dark)', fontWeight: 'var(--fw-bold)' }}>Tentar novamente</Button>}
      />
    );
  } else if (st === 'empty' && vm.emptyState) {
    body = <EmptyHero empty={vm.emptyState} a={a} />;
  } else if (detail) {
    body = <PlaceDetail vm={vm} detail={detail} a={a} headRef={detailHeadRef} rootRef={detailRootRef} single={single} />;
  } else if (items.length === 0) {
    const e = vm.emptyState;
    showSummary = true;
    showFilters = true;
    body = (
      <StateBlock
        icon={<SearchOffRounded sx={{ fontSize: 30, color: 'var(--text-secondary)' }} />}
        title={e?.title || 'Nada encontrado'}
        description={e?.body}
        action={e?.actionLabel ? (
          <Button onClick={() => a.onClearFilters?.()} startIcon={<FilterAltOffRounded sx={{ fontSize: 18 }} />} sx={{ ...OUTLINE_BTN_SX, minHeight: 42 }}>{e.actionLabel}</Button>
        ) : null}
      />
    );
  } else if (single) {
    const it = items[0];
    body = <Portrait item={it} a={a} cardRef={regCard(it.id)} openRef={regOpen(it.id)} />;
  } else {
    showSummary = true;
    showFilters = true;
    body = (
      <Box component="section" aria-label={labels.title} sx={CARDS_GRID}>
        {items.map((it, i) => (
          <PlaceCard
            key={it.id}
            item={it}
            a={a}
            cardRef={regCard(it.id)}
            openRef={regOpen(it.id)}
            flash={flash === it.id}
            delay={Math.min(i * 50, 400)}
          />
        ))}
      </Box>
    );
  }

  const showNew = st !== 'empty' && st !== 'error';

  return (
    // O fim da página tem a folga do aviso do container: o último botão sempre pode subir acima dele.
    <Box sx={{ color: 'var(--text-primary)', pb: FOLGA }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 'var(--space-3)' }}>
        <Box sx={{ minWidth: 0, flex: '1 1 320px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <StorefrontRounded sx={{ fontSize: 30, color: 'var(--color-primary-dark)' }} aria-hidden="true" />
            <Typography ref={pageTitleRef} tabIndex={-1} variant="h1" component="h1" sx={{ ...FOCUS_TITLE_SX, color: 'var(--text-primary)', textWrap: 'pretty' }}>
              {labels.title}
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: 'var(--text-secondary)', mt: 0.5, maxWidth: 640 }}>{labels.subtitle}</Typography>
        </Box>
        {showNew && <NewPlace labels={labels} plan={vm.planUsage} a={a} />}
      </Box>

      {showSummary && <SummaryBand summary={vm.summary} items={items} onJump={jump} firstAlert={firstAlert} />}
      {showFilters && <FiltersBar filters={f} labels={labels} a={a} />}
      {body}
    </Box>
  );
}
